import {
    Asset,
    Checklist,
    DocumentType,
    DocumentDetails,
    Invoice,
    Measurement,
    Order,
    Partnership,
    Query,
    Role,
    RoleAssignment,
    Site,
    Task,
    getEntityType,
    BusinessRule,
    BusinessRuleRuntimeStatus,
    Conversation,
    KnowledgeBase,
    MarketplaceOffer,
    Resource,
} from "@the-real-insight/tri-model";
import { configuration } from "./Configuration";
import {
  ApplicationError,
  FileTooLargeError,
  TaskCompletionInterruptedError,
  UnauthorizedError,
} from "./general/Errors";
import {
    authenticatedFetch,
    getAuthenticationFromToken,
    handleAuthenticatedResponse,
    jwtAuthService,
} from "./JWTAuthService";
import { storageAPI } from "./StorageAPI";
import { buildToolsFilterWithSelectedTool } from "./utils/customerTools";
import { PATIENT_REQUEST } from "./utilities/requestTimeout";

/** Same rules as tri-server GET mask for tool-configuration password fields; omit before PUT. */
function isToolConfigurationMaskPlaceholder(value: string): boolean {
  const s = String(value ?? "").trim();
  if (s === "") return false;
  if (/^\*{3,}$/.test(s)) return true;
  if (/^•{3,}$/.test(s)) return true;
  if (s.length >= 6 && /^.{3}\*{3,}$/.test(s)) return true;
  return false;
}

function getPasswordFieldIdSet(
  configurationSchema?: Array<{ id?: string; type?: string }>,
): Set<string> {
  if (!Array.isArray(configurationSchema) || configurationSchema.length === 0) {
    return new Set<string>();
  }

  return new Set(
    configurationSchema
      .filter(
        (field) => field?.type === "password" && typeof field?.id === "string",
      )
      .map((field) => field.id as string),
  );
}

function omitMaskedToolConfigurationValues(
  schemaConfiguration: Record<string, string>,
  passwordFieldIds: Set<string>,
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(schemaConfiguration)) {
    const normalized = String(v ?? "");
    if (
      passwordFieldIds.has(k) &&
      isToolConfigurationMaskPlaceholder(normalized)
    ) {
      continue;
    }
    out[k] = normalized;
  }
  return out;
}

const baseUrl = configuration.serverUrl;
// Per customer: the classes carry the attributes the customer added (Betreiberpflichten →
// Datenmodell), so another login in the same tab must not see the previous customer's.
let din276MetaData: { customer: string; data: Promise<any> } | null = null;

const handleResponse = async (
  response: any,
  callback: (entity: any) => void,
) => {
  if (response.ok) {
    // Some endpoints answer OK with an empty body (e.g. 204 No Content, or a
    // 200 with no rows). `response.json()` throws "Unexpected end of JSON input"
    // on an empty body, so read text first and only parse when non-empty.
    const text = await response.text();
    const result = text ? JSON.parse(text) : null;

    if (result) {
      return callback(result);
    } else {
      return null;
    }
  } else {
    if (response.status === 400) {
      throw new ApplicationError((await response.json()).message);
    } else if (response?.status === 401) {
      throw new UnauthorizedError();
    }
  }
};

export const handleResponseArray = async (
  response: any,
  callback: (entity: any) => void,
): Promise<any[]> => {
  if (response.ok) {
    // Guard against empty bodies (204 / no rows): `.json()` throws on empty input.
    const text = await response.text();
    const result = text ? JSON.parse(text) : null;

    if (result && Array.isArray(result)) {
      return result.map((entity: any) => callback(entity));
    } else if (result) {
      // If result is not an array but exists, wrap it in an array
      return [callback(result)];
    } else {
      return [];
    }
  } else {
    if (response.status === 400) {
      throw new ApplicationError((await response.json()).message);
    } else if (response?.status === 401) {
      throw new UnauthorizedError();
    }
    throw new Error(`Request failed with status ${response.status}`);
  }
};

const getResourceURL = (url: string) => {
  if (!url) {
    return "";
  }
  if (url.startsWith("http://") || url.startsWith("https://")) {
    return url;
  }
  return `${baseUrl}${url}`;
};

const getFileURL = (customerID: string, url: string) => {
  return `${baseUrl}/${customerID}/files${url}`;
};

const getChartImageURL = (customerID: string, resourceId: string) => {
  return `${baseUrl}/api/${customerID}/chart-image/${resourceId}`;
};

const getChartImageURLWithAuth = (customerID: string, resourceId: string) => {
  // This version includes authentication headers for direct access
  const token = jwtAuthService.getAccessToken();
  return `${baseUrl}/api/${customerID}/chart-image/${resourceId}?token=${token}`;
};

const storeChartImage = async (
  customerID: string,
  resourceId: string,
  imageData: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/${customerID}/chart-image/${resourceId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        resourceId,
        imageData,
        timestamp: Date.now(),
      }),
    },
  );

  return response.json();
};

const getResourceImageURL = (
  customerID: string,
  resourceType: string,
  resourceId: string,
) => {
  return `${baseUrl}/api/${customerID}/resource-image/${resourceType}/${resourceId}`;
};

const getResourceImageURLWithAuth = (
  customerID: string,
  resourceType: string,
  resourceId: string,
) => {
  // This version includes authentication headers for direct access
  const token = jwtAuthService.getAccessToken();
  return `${baseUrl}/api/${customerID}/resource-image/${resourceType}/${resourceId}?token=${token}`;
};

const storeResourceImage = async (
  customerID: string,
  resourceType: string,
  resourceId: string,
  imageData: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/${customerID}/resource-image/${resourceType}/${resourceId}`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        resourceId,
        resourceType,
        imageData,
        timestamp: Date.now(),
      }),
    },
  );

  return response.json();
};

/**
 * Branding never changes within a session, so the response is cached per tenant
 * for the lifetime of the page. Cached as the promise, so concurrent callers
 * share the single in-flight request rather than racing two of them.
 */
const themeCache = new Map<string, Promise<any>>();

const getTheme = async (tenantId: string) => {
  const cached = themeCache.get(tenantId);
  if (cached) return cached;
  const request = fetchTheme(tenantId);
  themeCache.set(tenantId, request);
  // A failed read must not be remembered — the next caller should retry.
  request.catch(() => themeCache.delete(tenantId));
  return request;
};

const fetchTheme = async (tenantId: string) => {
  const endpoint = `${baseUrl}/${tenantId}/theme`;
  console.log("🔍 GET THEME API CALL:", {
    tenantId,
    endpoint,
    fullURL: endpoint,
  });

  // Theme is a public branding read — it must succeed for unauthenticated
  // visitors hitting a branded entry point (e.g. /login?tenant=<id>), so we
  // use plain fetch instead of authenticatedFetch (which returns undefined
  // when no JWT is present and would crash the caller on response.status).
  const headers: Record<string, string> = { "Content-Type": "application/json" };
  const token = jwtAuthService.getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  const response = await fetch(endpoint, { method: "GET", headers });

  console.log("📡 GET THEME RESPONSE:", {
    status: response.status,
    ok: response.ok,
    statusText: response.statusText,
  });

  return handleAuthenticatedResponse(response, (result: any) => {
    if (result && result.error) {
      console.error("🚨 GET THEME API ERROR:", result.error);
      throw new ApplicationError(result.error);
    }
    console.log("✅ GET THEME SUCCESS:", {
      hasResult: !!result,
      hasLogoURL: !!result?.logoURL,
      logoURL: result?.logoURL,
      resultKeys: result ? Object.keys(result) : [],
    });
    return result;
  });
};

/** Geoapify-backed address autocomplete (server holds the API key) — same
 *  mechanism as tri-app. Returns suggestions with flat address fields
 *  { formatted, street, streetNumber, postalCode, city, country, lat, lon }. */
/**
 * Address search. Without `countryCode` the server covers Germany and its German-speaking
 * neighbours (DE, AT, CH, LI); a fixed "de" here hid every Swiss address from the object selection.
 */
const getGeocodeAutocomplete = async (text: string, limit = 8, countryCode?: string) => {
  const country = countryCode ? `&countryCode=${encodeURIComponent(countryCode)}` : "";
  const response = await authenticatedFetch(
    `${baseUrl}/geocode/autocomplete?text=${encodeURIComponent(text.trim())}&limit=${limit}${country}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
  );

  if (!response.ok) {
    throw new Error(`geocode/autocomplete failed: ${response.status}`);
  }

  const result = await response.json();

  return result?.suggestions ?? [];
};

/**
 * Normalize country code for building matching: uppercase two-letter ISO codes
 * to match the format used in composite Building.id keys.
 */
function normalizeCountryCode(country: unknown): string {
  if (country == null) {
    return "DE";
  }
  if (typeof country === "string") {
    const trimmed = country.trim();
    if (trimmed === "") {
      return "DE";
    }
    return trimmed.length === 2 ? trimmed.toUpperCase() : trimmed;
  }
  return String(country);
}

/**
 * Read-only building match: returns the single existing building for an address
 * (exact composite id → canonical key → street number → geo proximity, with the
 * street+house-number safeguard), or null. Replaces the old client-side relaxed
 * address queries, which matched the whole OSM footprint corpus on a street.
 * `address` carries flat fields { street, streetNumber, postalCode, city,
 * country, lat, lon } (e.g. from the AddressPicker).
 */
const matchBuilding = async (address: {
  street?: string;
  streetNumber?: string;
  postalCode?: string;
  city?: string;
  country?: unknown;
  lat?: number;
  lon?: number;
}): Promise<any | null> => {
  const response = await authenticatedFetch(`${baseUrl}/buildings/match`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      street: address.street ?? "",
      streetNumber: address.streetNumber ?? "",
      postalCode: address.postalCode ?? "",
      city: address.city ?? "",
      country: normalizeCountryCode(address.country),
      lat: address.lat,
      lon: address.lon,
    }),
  });

  if (!response.ok) {
    if (response.status === 404) {
      return null;
    }
    throw new Error(`buildings/match failed: ${response.status}`);
  }

  const result = await response.json();
  return result?.building ?? null;
};

/**
 * Get-or-create the building for an address via POST /buildings/ensure: runs the
 * same matcher as matchBuilding, but creates the building when none exists.
 * Use this for the attach flow (linking a building to a new site); use
 * matchBuilding for read-only lookups that must not mint buildings.
 */
const ensureBuilding = async (
  address: {
    street?: string;
    streetNumber?: string;
    postalCode?: string;
    city?: string;
    country?: unknown;
    lat?: number;
    lon?: number;
  },
  /**
   * The site the building belongs to, where the caller knows it. The server records the link, so
   * the connection survives a reload instead of being re-derived by comparing address strings.
   */
  siteId?: string,
): Promise<any | null> => {
  const response = await authenticatedFetch(`${baseUrl}/buildings/ensure`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      street: address.street ?? "",
      streetNumber: address.streetNumber ?? "",
      postalCode: address.postalCode ?? "",
      city: address.city ?? "",
      country: normalizeCountryCode(address.country),
      lat: address.lat,
      lon: address.lon,
      ...(siteId ? { site: siteId } : {}),
    }),
  });

  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    throw new Error(`buildings/ensure failed: ${response.status}`);
  }

  const result = await response.json();
  return result?.building ?? null;
};

const currentCustomer = () => getAuthenticationFromToken()?.customer ?? "";

/** Drops the cached classes — after the customer's data model changed. */
const invalidateDIN276Metadata = () => {
  din276MetaData = null;
};

const getDIN276Metadata = async () => {
  const customer = currentCustomer();
  if (din276MetaData && din276MetaData.customer === customer) {
    return din276MetaData.data;
  }

  // Instantiable KGs only — "DIN 276 KG === instantiable". Every consumer of this
  // shared/cached method (category pickers + code→category resolver maps) works on
  // real cost groups; none descends into the synthetic K.*/T.*/F. layer or the
  // provisional placeholder nodes. The admin DIN 276 tree fetches the full graph
  // separately (its own `?excludeSynthetic=false&excludePending=false` call).
  // Sent with the login, the classes include the customer's own attributes (`source: "tenant"`).
  const response = await authenticatedFetch(`${baseUrl}/metadata/din276?excludeSynthetic=true&excludePending=true`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (response.status === 200) {
    const data = response.json();
    din276MetaData = { customer, data };
    return data;
  } else {
    throw new Error("Unspecified error.");
  }
};

/**
 * Full metadata including synthetic and pending nodes — used by DinPropertySheet to support
 * assets whose schemaCategory references provisional categories. With `normOnly`, without the
 * customer's own attributes: for the views that show the norm and the additions apart.
 */
const getDIN276MetadataFull = async ({ normOnly = false }: { normOnly?: boolean } = {}) => {
  const response = await authenticatedFetch(
    `${baseUrl}/metadata/din276?excludeSynthetic=false&excludePending=false${normOnly ? "&normOnly=true" : ""}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (response.status === 200) {
    return response.json();
  } else {
    throw new Error("Unspecified error.");
  }
};

const getPlatformServicesMarketplaceOffer =
  async (): Promise<MarketplaceOffer> => {
    // Try with regular fetch first (for registration flow)
    let response;
    try {
      response = await fetch(
        `${baseUrl}/marketplace-offers/platform-services`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      if (!response.ok && response.status === 401) {
        // If unauthenticated, try with authenticated fetch
        response = await authenticatedFetch(
          `${baseUrl}/marketplace-offers/platform-services`,
          {
            method: "GET",
            headers: {
              "Content-Type": "application/json",
            },
          },
        );
      }
    } catch (error) {
      // If fetch fails, try with authenticated fetch
      response = await authenticatedFetch(
        `${baseUrl}/marketplace-offers/platform-services`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
          },
        },
      );
    }

    const result = await handleResponse(response, (result: any) => {
      // @ts-ignore
      return new (getEntityType(result._type).type.type)(result);
    });

    if (!result) {
      throw new Error("Failed to fetch platform services marketplace offer");
    }

    return result;
  };

const getPostalCode = async (postalCode: string): Promise<any> => {
  const response = await fetch(`${baseUrl}/postal-codes/${postalCode}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (response.ok) {
    return response.json();
  } else if (response.status === 404) {
    return null;
  } else {
    throw new Error("Failed to fetch postal code");
  }
};

const getCustomer = async (customerId: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/customers/${customerId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return response.json();
};

const getCustomerTools = async (customerId: string) => {
  try {
    const response = await authenticatedFetch(
      `${baseUrl}/customers/${customerId}/tools`,
      {
        method: "GET",
      },
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error(
        `getCustomerTools API error: ${response.status} ${response.statusText}`,
        errorText,
      );

      // Handle specific error cases
      if (response.status === 500) {
        // Check if it's an MCP timeout error
        if (errorText.includes("MCP loading timeout")) {
          console.warn(
            "MCP loading timeout detected, returning empty tools array",
          );
          return [];
        }
        // Check if it's a circular JSON error
        if (errorText.includes("Converting circular structure to JSON")) {
          console.warn(
            "Circular JSON structure error detected, returning empty tools array",
          );
          return [];
        }
      }

      throw new Error(
        `API request failed with status ${response.status}: ${errorText}`,
      );
    }

    const result = await handleAuthenticatedResponse(response);
    console.log("getCustomerTools payload:", {
      count: Array.isArray(result) ? result.length : 0,
      sample: Array.isArray(result) ? result.slice(0, 3) : result,
    });
    return result;
  } catch (error) {
    // Handle network errors and other exceptions
    if (
      error instanceof TypeError &&
      error.message.includes("Failed to fetch")
    ) {
      console.warn(
        "Network error when fetching customer tools, returning empty array",
      );
      return [];
    }

    // Re-throw other errors
    throw error;
  }
};

const getUsers = async () => {
  const response = await authenticatedFetch(`${baseUrl}/users`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  // Check if response is ok and has content
  if (!response.ok) {
    throw new Error(
      `Failed to fetch users: ${response.status} ${response.statusText}`,
    );
  }

  const text = await response.text();
  if (!text) {
    return [];
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    console.error("Failed to parse users response:", text);
    throw new Error("Invalid JSON response from server");
  }
};

const getBridges = async (query: any) => {
  const response = await authenticatedFetch(`${baseUrl}/bridges`, {
    method: "POST",
    body: JSON.stringify(query),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return response.json();
};

const getBuildings = async (query: any) => {
  const response = await authenticatedFetch(`${baseUrl}/buildings`, {
    method: "POST",
    body: JSON.stringify(query),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return response.json();
};

const submitRequest = async (
  conversation: string,
  agent: string,
  profile: string,
  input: string,
  context: any,
) => {
  const response = await authenticatedFetch(`${baseUrl}/submit`, {
    method: "POST",
    body: JSON.stringify({
      conversation,
      agent,
      profile,
      input,
      context,
    }),
  });

  return handleAuthenticatedResponse(response, (data: any) => {
    data.resources = data.resources
      ? data.resources.map((resource: any) => {
          resource.queries = (resource.queries || []).map((query: any) =>
            Query.unmarshal(query),
          );
          return resource;
        })
      : undefined;
    return data;
  });
};

const streamPromptResponse = (
  conversation: string,
  agent: string,
  profile: string,
  input: string,
  context: any,
  toolsFilter?: any,
  signal?: AbortSignal,
  streamOptions?: {
    runId?: string;
    resumeFromChunk?: number;
    reconnect?: boolean;
  },
  selectedToolId?: string,
) => {
  const effectiveToolsFilter = buildToolsFilterWithSelectedTool(
    toolsFilter,
    selectedToolId,
  );

  console.log(
    "Tools filter >>>",
    effectiveToolsFilter || "No tools filter specified",
    selectedToolId ? `(selected tool: ${selectedToolId})` : "",
  );

  // Add a unique timestamp to force a new connection and prevent connection reuse issues
  const timestamp = Date.now();
  const randomId = Math.random().toString(36).substring(2, 15);

  const requestBody: any = {
    conversation,
    agent,
    profile,
    context,
    toolsFilter: effectiveToolsFilter,
    demo: effectiveToolsFilter?.demo ?? toolsFilter?.demo,
    llm: context?.llm, // Extract llm from context and send as separate parameter
    // Which engine serves the turn: the router's single-tool path, or the agent loop.
    // A string, not a boolean, so 'auto' can arrive later without changing the contract.
    engine: context?.engine,
  };

  if (selectedToolId) {
    requestBody.toolId = selectedToolId;
  }

  if (!streamOptions?.reconnect) {
    requestBody.input = input;
  } else {
    requestBody.runId = streamOptions?.runId;
    requestBody.resumeFromChunk = streamOptions?.resumeFromChunk ?? 0;
  }

  return authenticatedFetch(
    `${baseUrl}/skills/prompt/stream?_t=${timestamp}&_r=${randomId}`,
    {
      method: "POST",
      body: JSON.stringify(requestBody),
      signal,
    },
  );
};

const triggerEvent = async (id: string) => {
  const response = await authenticatedFetch(`${baseUrl}/admin/trigger`, {
    method: "POST",
    body: JSON.stringify({ id }),
    headers: {
      "Content-Type": "application/json",
      //"authorization": "Bearer " + authentication.access_token
    },
  });

  return response.json();
};

const checkEmail = async (
  email: string,
  firstName: string,
  lastName: string,
) => {
  const response = await fetch(`${baseUrl}/registration/checkEmail`, {
    method: "POST",
    body: JSON.stringify({ email, firstName, lastName }),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  if (response.status === 200) {
    const result: any = await response.json();

    if (result.error) {
      throw new ApplicationError(result.error);
    } else {
      return result;
    }
  } else {
    throw new UnauthorizedError(response.status);
  }
};

const checkUser = async (username: string) => {
  const response = await fetch(`${baseUrl}/checkUser`, {
    method: "POST",
    body: JSON.stringify({ email: username }),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  if (response.ok) {
    const result: any = await response.json();

    console.log(result);

    if (result.error) {
      throw new ApplicationError(result.error);
    } else {
      return result;
    }
  } else {
    if (response.status === 401) {
      throw new UnauthorizedError();
    }

    throw new Error();
  }
};

const validateEmail = async (email: string, validationCode: string) => {
  const response = await fetch(`${baseUrl}/registration/validateEmail`, {
    method: "POST",
    body: JSON.stringify({ email, validationCode }),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  if (response.ok) {
    const result: any = await response.json();

    if (result.error) {
      throw new ApplicationError(result.error);
    } else {
      return result;
    }
  } else {
    throw Error("Server error.");
  }
};

const registerAndOrder = async (
  user: any,
  customer: any,
  legalPerson: any,
  plans: any,
  paymentMethod: any,
) => {
  const response = await fetch(`${baseUrl}/registration/registrationAndOrder`, {
    method: "POST",
    body: JSON.stringify({ user, customer, legalPerson, plans, paymentMethod }),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  if (response.ok) {
    return response.json();
  } else {
    if (response?.status === 401) {
      throw new UnauthorizedError();
    } else {
      throw Error("Server error.");
    }
  }
};

const login = async (email: string, password: string) => {
  const response = await fetch(`${baseUrl}/login`, {
    method: "POST",
    body: JSON.stringify({ email, password }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (response.ok) {
    const result: any = await response.json();

    if (result.error) {
      throw new ApplicationError(result.error);
    } else {
      // Store JWT tokens if they are provided (now always provided with new protocol)
      if (result.accessToken && result.refreshToken) {
        jwtAuthService.setTokens({
          accessToken: result.accessToken,
          refreshToken: result.refreshToken,
        });

        // Log authentication state for debugging
        console.log("Login successful with JWT tokens. Auth state:", {
          passwordChangeRequired: result.passwordChangeRequired,
          mfaRequired: result.mfaRequired,
        });
      }
      return result;
    }
  } else {
    if (response.status === 401) {
      throw new UnauthorizedError();
    }
    // The organization signs in with Microsoft only (tri-server docs/entra-sso.md, "Nur SSO").
    if (response.status === 403 && (await response.json().catch(() => null))?.error === 'sso_required') {
      throw new ApplicationError('entraSso.ssoRequired');
    }

    throw new Error();
  }
};

const checkAuthentication = async () => {
  const accessToken = jwtAuthService.getAccessToken();

  if (!accessToken) {
    return false;
  }

  // Check if token is expired
  if (jwtAuthService.isTokenExpired(accessToken)) {
    // Try to refresh the token
    const newToken = await jwtAuthService.refreshAccessToken();
    if (!newToken) {
      return false;
    }
  }

  // Token is valid, consider user authenticated
  return true;
};

const sendOTP = async () => {
  const response = await authenticatedFetch(`${baseUrl}/otp`, {
    method: "GET",
  });

  return handleAuthenticatedResponse(response);
};

const checkOTP = async (email: string, otp: string) => {
  const response = await authenticatedFetch(`${baseUrl}/otp`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify({ email, otp }),
  });

  const result = (await handleAuthenticatedResponse(response)) as any;

  // Store JWT tokens if they are provided
  if (result.success && result.jwtToken) {
    jwtAuthService.setTokens({
      accessToken: result.jwtToken,
      refreshToken: result.refreshToken || "",
    });
  }

  return result;
};

const updateUserCredentials = async (
  customerId: string,
  userId: string,
  oldPassword: string,
  newPassword: string,
  mobileNumber?: string,
) => {
  console.log("updateUserCredentials called with:", {
    customerId,
    userId,
    oldPassword: "***",
    newPassword: "***",
    mobileNumber,
  });

  // Debug: Check if JWT token is available
  const accessToken = jwtAuthService.getAccessToken();
  console.log("JWT access token available:", !!accessToken);
  if (accessToken) {
    console.log(
      "JWT token expired:",
      jwtAuthService.isTokenExpired(accessToken),
    );
  }

  // Use authenticatedFetch with JWT token (new protocol always provides JWT tokens)
  const response = await authenticatedFetch(
    `${baseUrl}/customers/${customerId}/users/${userId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ oldPassword, newPassword, mobileNumber }),
    },
  );

  const result = await handleAuthenticatedResponse(response);

  console.log("Password update response:", result);

  // If the server returns new JWT tokens after successful password update, store them
  if (
    result &&
    typeof result === "object" &&
    "accessToken" in result &&
    "refreshToken" in result
  ) {
    console.log("New JWT tokens received after password update, storing them");
    jwtAuthService.setTokens({
      accessToken: (result as any).accessToken,
      refreshToken: (result as any).refreshToken,
    });
  } else {
    console.log("No new JWT tokens in password update response");
  }

  return result;
};

const logout = async () => {
  // Save the current access token before clearing, so we can still authenticate
  // the logout request while eliminating the race window where tokens remain
  // valid after the user initiates logout.
  const accessToken = jwtAuthService.getAccessToken();
  
  // Clear tokens immediately to prevent any race condition where protected routes
  // still see a valid JWT after the user has clicked logout but before the async
  // logout request completes.
  jwtAuthService.clearTokens();

  // If no token was present, return immediately
  if (!accessToken) {
    return { success: true };
  }

  // Make the logout request with the saved token. We use plain fetch instead of
  // authenticatedFetch (which would try to get a token that we just cleared) and
  // pass the Authorization header explicitly.
  try {
    const response = await fetch(`${baseUrl}/logout`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
    });
    return response.json();
  } catch (error) {
    // Server logout failed, but local tokens are already cleared.
    console.warn("Server logout failed, but local tokens already cleared:", error);
    return { success: true };
  }
};

const getCustomerSettings = async (id: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/customers/${id}/settings`,
    {
      method: "GET",
    },
  );

  return handleAuthenticatedResponse(response);
};

const sendEmail = async (mail: any) => {
  const response = await authenticatedFetch(`${baseUrl}/email`, {
    method: "POST",
    body: JSON.stringify(mail),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return handleAuthenticatedResponse(response);
};

const sendSMS = async (phoneNumber: string, message: string) => {
  const response = await authenticatedFetch(`${baseUrl}/sms`, {
    method: "POST",
    body: JSON.stringify({ phoneNumber, message }),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return handleAuthenticatedResponse(response);
};

const getMarkup = async (path: string) => {
  const response = await fetch(`${baseUrl}/markup/${path}`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return response.text();
};

/** Validate BPMN XML via server (tri-bpmn-engine). Returns { issues: ValidationIssue[] }. */
const validateBpmn = async (
  customerID: string,
  bpmnXml: string,
): Promise<
  {
    rule?: string;
    severity?: string;
    elementId?: string;
    elementIds?: string[];
    elementType?: string;
    message?: string;
  }[]
> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/bpm/validate`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ bpmnXml }),
    },
  );
  if (!response.ok) {
    if (response.status === 503) {
      console.warn("BPM validation unavailable (engine not configured)");
      return [];
    }
    throw new Error(`BPM validation failed: ${response.statusText}`);
  }
  const data = await response.json();
  return data?.issues ?? [];
};

/**
 * Ask a question about a process conversation without moving the process.
 *
 * Deliberately NOT the message endpoint: writing into a process conversation answers its open task.
 * This one only reads — the server retrieves over the attached documents and synthesises an answer,
 * and persists nothing.
 */
const askConversation = async (
  customerID: string,
  conversationId: string,
  question: string,
): Promise<{ answer: string; documentCount: number; sources?: any[] }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationId}/ask`,
    {
      method: "POST",
      body: JSON.stringify({ question }),
      headers: { "Content-Type": "application/json" },
    },
  );
  return handleAuthenticatedResponse(response);
};

/** The caller's own follow-up thread for a conversation — never anyone else's. */
const getConversationAskThread = async (
  customerID: string,
  conversationId: string,
): Promise<{ entries: { question: string; answer: string; at: string }[] }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationId}/ask`,
    { method: "GET" },
  );
  return handleAuthenticatedResponse(response);
};

const clearConversationAskThread = async (
  customerID: string,
  conversationId: string,
): Promise<{ entries: [] }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationId}/ask`,
    { method: "DELETE" },
  );
  return handleAuthenticatedResponse(response);
};

const getBpmSchedules = async (customerID: string): Promise<any[]> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/bpm/schedules`,
    { method: "GET" },
  );
  return handleAuthenticatedResponse(
    response,
    (data: any) => data?.workflows ?? [],
  );
};

const activateBpmSchedule = async (
  customerID: string,
  workflowId: string,
): Promise<any> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/bpm/schedules/${workflowId}/activate`,
    { method: "POST" },
  );
  return handleAuthenticatedResponse(response, (data: any) => data);
};

const deactivateBpmSchedule = async (
  customerID: string,
  workflowId: string,
): Promise<any> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/bpm/schedules/${workflowId}/deactivate`,
    { method: "POST" },
  );
  return handleAuthenticatedResponse(response, (data: any) => data);
};

/** Fire-time telemetry for a single schedule (most recent first). */
const getBpmScheduleFireHistory = async (
  customerID: string,
  scheduleId: string,
  options?: { limit?: number; outcome?: "ok" | "error" },
): Promise<any[]> => {
  const params = new URLSearchParams();
  if (options?.limit != null) params.set("limit", String(options.limit));
  if (options?.outcome) params.set("outcome", options.outcome);
  const q = params.toString();
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/bpm/schedules/${scheduleId}/fires${q ? `?${q}` : ""}`,
    { method: "GET" }
  );
  return handleAuthenticatedResponse(response, (data: any) => data?.items ?? []);
};

/** BPM process instances (Mongo `ProcessInstances` for tenant). */
const getProcessInstances = async (
  customerID: string,
  options?: { status?: string; limit?: number },
): Promise<any[]> => {
  const params = new URLSearchParams();
  if (options?.status) params.set("status", options.status);
  if (options?.limit != null) params.set("limit", String(options.limit));
  const q = params.toString();
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/process-instances${q ? `?${q}` : ""}`,
    { method: "GET" },
  );
  return handleAuthenticatedResponse(
    response,
    (data: any) => data?.items ?? [],
  );
};

/** Single process instance: engine snapshot, tasks, conversation, data pool, document refs. */
const getProcessInstance = async (
  customerID: string,
  instanceId: string,
): Promise<any> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/process-instances/${encodeURIComponent(instanceId)}`,
    { method: "GET" },
  );
  return handleAuthenticatedResponse(response, (data: any) => data);
};

const deleteProcessInstance = async (
  customerID: string,
  instanceId: string,
): Promise<{ purgedInstanceIds?: string[] }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/process-instances/${encodeURIComponent(instanceId)}`,
    { method: "DELETE" },
  );
  return handleAuthenticatedResponse(response, (data: any) => data);
};

/**
 * Purge ALL of a customer's BPM process instances (platform-admin action).
 * Server: DELETE /:customer/process-instances (Permission.PROCESS_INSTANCES_DELETE).
 */
const deleteAllProcessInstances = async (
  customerID: string,
): Promise<{
  matched?: number;
  purgedInstanceIds?: string[];
  subProcessLinksDeleted?: number;
  dataPoolsPurged?: number;
  alreadyGone?: string[];
  failed?: string[];
}> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/process-instances`,
    { method: "DELETE" },
  );
  return handleAuthenticatedResponse(response, (data: any) => data);
};

const getBusinessRules = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/business-rules`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return handleResponse(response, (result: any) => {
    // tri-model's class drops `cooldownMinutes` (tri-server #1488); keep it beside the instance so
    // the alert list can show it and the editor can send it back.
    return result.map((businessRule: any) => {
      const rule = new BusinessRule(businessRule);

      return businessRule?.cooldownMinutes === undefined
        ? rule
        : Object.assign(rule, { cooldownMinutes: businessRule.cooldownMinutes });
    });
  });
};

const createBusinessRule = async (
  customerID: string,
  businessRule: BusinessRule,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/business-rules`,
    {
      body: JSON.stringify(businessRule),
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return new BusinessRule(result);
  });
};

const updateBusinessRule = async (
  customerID: string,
  businessRule: BusinessRule,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/business-rules/${businessRule._id}`,
    {
      body: JSON.stringify(businessRule),
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return new BusinessRule(result);
  });
};

const activateBusinessRule = async (
  customerID: string,
  businessRule: BusinessRule,
) => {
  return updateBusinessRule(
    customerID,
    new BusinessRule({
      ...businessRule,
      status: BusinessRuleRuntimeStatus.running,
    }),
  );
};

const deactivateBusinessRule = async (
  customerID: string,
  businessRule: BusinessRule,
) => {
  return updateBusinessRule(
    customerID,
    new BusinessRule({
      ...businessRule,
      status: BusinessRuleRuntimeStatus.stopped,
    }),
  );
};

/**
 * The server answers with the tenant's remaining rules (tri-server #1184). Older servers answered
 * with the deleted rule as an object, and `result.map` on that was the TypeError behind the row
 * that would not go away — `handleResponseArray` copes with either shape, and unlike
 * `handleResponse` it throws on a 404 (already gone, or not this tenant's) instead of returning
 * `undefined` as if the delete had worked.
 */
const deleteBusinessRule = async (
  customerID: string,
  businessRule: BusinessRule,
): Promise<BusinessRule[]> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/business-rules/${businessRule._id}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return handleResponseArray(
    response,
    (result: any) => new BusinessRule(result),
  );
};

// >>> Move to Query

const bindInverseRelationships = (query: Query, rootEntity: any) => {
  return rootEntity;

  // let previousEntityTypeName = query.entityQuery.type.name;
  // let previousEntity = rootEntity;

  // query.linkedEntityQueries.forEach((linkedEntityQuery: LinkedEntityQuery) => {
  //     if (!linkedEntityQuery.relationship) {
  //         return;
  //     }

  //     logDebug('Retrieving relationship ', previousEntityTypeName, linkedEntityQuery.relationship);

  //     const linkedEntity = record.get(`${linkedEntityQuery.name || previousEntityTypeName /* hack */}`).properties;

  //     // Convert big numbers

  //     this.convertBigNums(linkedEntity);

  //     // @ts-ignore
  //     // logDebug('Linked entity ', linkedEntityQuery.type.name);
  //     logDebug(linkedEntity);
  //     // logDebug(previousEntityTypeName);
  //     // logDebug(previousEntity);

  //     let relationship;

  //     if (linkedEntityQuery.direction === RelationshipDirection.out) {
  //         relationship = getRelationship(previousEntityTypeName, linkedEntityQuery.relationship);

  //         // logDebug(relationship);
  //         // logDebug(`-[:${linkedEntityQuery.relationship}]->`);

  //         if (relationship.cardinality === Cardinality.ManyToMany ||
  //             relationship.cardinality === Cardinality.OneToMany) {
  //             previousEntity[relationship.name] = previousEntity[relationship.name] || [];

  //             previousEntity[relationship.name].push(linkedEntity)
  //         } else {
  //             previousEntity[relationship.name] = linkedEntity;
  //         }

  //         // We only set the forward references here. Setting both sides would lead to circular references during marshalling.
  //         // If need be, we can easily bind the reverse relationship on the client

  //         // if (relationship.directionality === Directionality.Bidirectional && relationship.inverseName) {
  //         //     if (relationship.cardinality === Cardinality.ManyToMany ||
  //         //         relationship.cardinality === Cardinality.ManyToOne) {
  //         //         linkedEntity[relationship.inverseName] = linkedEntity[relationship.name] || [];

  //         //         linkedEntity[relationship.inverseName].push(previousEntity)
  //         //     } else {
  //         //         linkedEntity[relationship.inverseName] = previousEntity;
  //         //     }
  //         // }
  //     } else {
  //         // TODO Ugly
  //         // @ts-ignore
  //         relationship = getRelationship(linkedEntityQuery.type.name, linkedEntityQuery.relationship);

  //         // logDebug(relationship);
  //         // logDebug(`<-[:${linkedEntityQuery.relationship}]-`);

  //         // We only set the forward references here. Setting both sides would lead to circular references during marshalling.
  //         // If need be, we can easily bind the reverse relationship on the client

  //         // if (relationship.cardinality === Cardinality.ManyToMany ||
  //         //     relationship.cardinality === Cardinality.OneToMany) {
  //         //     linkedEntity[relationship.name] = linkedEntity[relationship.name] || [];

  //         //     linkedEntity[relationship.name].push(previousEntity)
  //         // } else {
  //         //     linkedEntity[relationship.name] = previousEntity;
  //         // }

  //         if (relationship.directionality === Directionality.Bidirectional && relationship.inverseName) {
  //             if (relationship.cardinality === Cardinality.ManyToMany ||
  //                 relationship.cardinality === Cardinality.ManyToOne) {
  //                 previousEntity[relationship.inverseName] = previousEntity[relationship.inverseName] || [];

  //                 previousEntity[relationship.inverseName].push(linkedEntity)
  //             } else {
  //                 previousEntity[relationship.inverseName] = linkedEntity;
  //             }
  //         }
  //     }

  //     previousEntityTypeName = relationship.getTypeName();
  //     previousEntity = linkedEntity;
  // });
};

// <<<

const query = async (
  query: Query,
  skip: number = 0,
  limit: number = 0,
): Promise<any[]> => {
  const response = await authenticatedFetch(`${baseUrl}/query`, {
    method: "POST",
    // skip/limit are optional server-side pagination; 0/0 = unbounded (previous
    // behaviour). Large listings pass a limit to avoid pulling the whole set.
    body: JSON.stringify({ ...query.marshal(), skip, limit }),
  });

  return handleAuthenticatedResponse(response, (json: any) => {
    if (query.fieldReferences && query.fieldReferences.length > 0) {
      console.log("response status >>>", response.status);
      return json;
    } else {
      const entityType: any = getEntityType(query.entityQuery.type.name);
      // API may return JSON null for an empty result; avoid crashing on .map
      const rows = json == null ? [] : Array.isArray(json) ? json : [];
      return rows.map((properties: any) =>
        bindInverseRelationships(query, new entityType(properties)),
      );
    }
  });
};

const queryOne = async (q: Query): Promise<any | null> => {
  const result = await query(q);

  if (!result || result.length === 0) {
    return null;
  }

  if (result.length > 1) {
    throw new Error("Query yields too many entities.");
  }

  return result[0];
};

//  @deprecated

const queryNoSQL = async (
  entityTypeName: string,
  filter: any,
): Promise<any[]> => {
  const response = await authenticatedFetch(
    `${baseUrl}/nosql/query/${entityTypeName}`,
    {
      method: "POST",
      body: JSON.stringify(filter),
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  const entityType: any = getEntityType(entityTypeName);
  const result = await response.json();

  console.log(entityType, result);

  return result.map((properties: any) => new entityType(properties));
};

const queryTelemetry = async (filter: any) => {
  const response = await authenticatedFetch(`${baseUrl}/telemetry/query`, {
    method: "POST",
    body: JSON.stringify(filter),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return ((await response.json()) as any).map(
    (properties: any) => new Measurement(properties),
  );
};

const queryZip = async (
  query: Query,
  options?: { grouped?: boolean }
): Promise<any> => {
  const url = options?.grouped
    ? `${baseUrl}/query/zip?grouped=true`
    : `${baseUrl}/query/zip`;
  const response = await authenticatedFetch(url, {
    method: "POST",
    body: JSON.stringify(query.marshal()),
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
    signal: PATIENT_REQUEST,
  });

  const x = await response.blob();

  return x;
};

const getEntityByID = async (entityTypeName: string, id: string) => {
  const entityType: any = getEntityType(entityTypeName);
  const response = await authenticatedFetch(
    `${baseUrl}/entities/${entityTypeName}/${id}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  const properties = await response.json();

  console.log("Properties >>>", properties);

  if (!response.ok) {
    throw new ApplicationError(
      (properties && typeof properties === "object" && properties.message) ||
        response.statusText ||
        "Entity request failed",
      response.status,
    );
  }

  if (
    properties === null ||
    properties === undefined ||
    typeof properties !== "object" ||
    Array.isArray(properties)
  ) {
    // The server answers an unknown id with 200 and `null`; 404 lets the page say "not found".
    throw new ApplicationError("Entity not found", 404);
  }

  const entity = new entityType(properties);
  // Set by the server when a manufacturer reads an asset of another tenant (read-only).
  if (properties._manufacturerView) (entity as any)._manufacturerView = true;

  console.log("Entity retrieved >>>", entity);

  return entity;
};

/**
 * Lightweight product list for the Product Management panel. A tenant can have
 * 60k+ products whose full docs carry heavy telemetry/conditions/properties/
 * documents JSON — fetching them all just to render a paginated table is slow.
 * This returns only the fields the list, charts and search need; the full
 * product is loaded on demand (getEntityByID) when a row is opened.
 */
const getProductStubs = async (): Promise<any[]> => {
  const response = await authenticatedFetch(`${baseUrl}/products/stubs`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });
  const rows = await handleAuthenticatedResponse(response);
  return Array.isArray(rows) ? rows : [];
};

/** One page of the Product Management listing (server-side paginated + searched). */
const getProductPage = async (params: {
  skip?: number;
  limit?: number;
  search?: string;
  categoryValues?: string[];
  vendor?: string;
  withDocuments?: boolean;
}): Promise<{ rows: any[]; total: number }> => {
  const response = await authenticatedFetch(`${baseUrl}/products/page`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params || {}),
  });
  const data: any = await handleAuthenticatedResponse(response);
  return {
    rows: Array.isArray(data?.rows) ? data.rows : [],
    total: Number(data?.total) || 0,
  };
};

/** One page of products with their photos, for the product-image editor (Einstellungen → Produktbilder). */
const getProductPhotoPage = async (params: {
  category?: string;
  purpose?: string;
  search?: string;
  onlyUnconfirmed?: boolean;
  skip?: number;
  limit?: number;
}): Promise<{ rows: any[]; total: number }> => {
  const response = await authenticatedFetch(`${baseUrl}/products/photos/page`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params || {}),
  });
  const data: any = await handleAuthenticatedResponse(response);
  return {
    rows: Array.isArray(data?.rows) ? data.rows : [],
    total: Number(data?.total) || 0,
  };
};

/** Move one photo of a product to a role (or confirm it); returns the product's new image + photos. */
const updateProductPhoto = async (
  productId: string,
  decision: { url: string; purpose?: string; main?: boolean },
): Promise<{ image: string | null; photos: any[] }> => {
  const response = await authenticatedFetch(`${baseUrl}/products/${encodeURIComponent(productId)}/photos`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(decision),
  });
  return handleAuthenticatedResponse(response);
};

/** Confirm many photos at once (e.g. everything left on the page). */
const confirmProductPhotos = async (items: { productId: string; url: string }[]): Promise<{ confirmed: number }> => {
  const response = await authenticatedFetch(`${baseUrl}/products/photos/confirm`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ items }),
  });
  return handleAuthenticatedResponse(response);
};

/** Category + vendor distribution counts for the Product Management charts (server aggregation). */
const getProductDistributions = async (params: {
  search?: string;
  categoryValues?: string[];
  vendor?: string;
  withDocuments?: boolean;
}): Promise<{ total: number; avgDocs: number; byCategory: any[]; byVendor: any[] }> => {
  const response = await authenticatedFetch(`${baseUrl}/products/distributions`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params || {}),
  });
  const data: any = await handleAuthenticatedResponse(response);
  return {
    total: Number(data?.total) || 0,
    avgDocs: Number(data?.avgDocs) || 0,
    byCategory: Array.isArray(data?.byCategory) ? data.byCategory : [],
    byVendor: Array.isArray(data?.byVendor) ? data.byVendor : [],
  };
};

/**
 * Products actually deployed in the given projects — the distinct products of the assets
 * standing at those projects' sites. Pass an empty array for "all projects of the tenant".
 * Use this instead of querying `Product` for picker options: that collection is the global
 * catalogue (>150k rows), far too large to offer as options.
 */
const getProductsOfProjects = async (
  projects: string[],
): Promise<{ _id: string; name: string }[]> => {
  const response = await authenticatedFetch(`${baseUrl}/products/of-projects`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ projects: projects || [] }),
  });
  const data: any = await handleAuthenticatedResponse(response);
  return Array.isArray(data) ? data : [];
};

/**
 * Building-use vocabulary (Legal.UsageClass) for the building master data form.
 * Served rather than hard-coded — the list carries its own MBO references and
 * has more entries than any spec sheet enumerates.
 */
const getUsageClasses = async (): Promise<{ usageClass: string; name: string; nameEn?: string }[]> => {
  const response = await authenticatedFetch(`${baseUrl}/legal/usage-classes`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  const data: any = await handleAuthenticatedResponse(response);
  return Array.isArray(data?.usageClasses) ? data.usageClasses : [];
};

const getSkills = async () => {
  const response = await fetch(`${baseUrl}/skills`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return response.json();
};

const getSampleRequests = async (customerID: string) => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/skills`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return response.json();
};

const getChatbotIdeas = async () => {
  const response = await authenticatedFetch(`${baseUrl}/chatbot/ideas`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  return handleAuthenticatedResponse(response, (data: any) => data);
};

const optimizeKnowledgeBaseDescription = async (
  customerID: string,
  kbId: string,
  language: 'de' | 'en'
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/knowledge-bases/${kbId}/optimize-description`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ language }),
    }
  );

  if (!response.ok) {
    const errText = await response.text().catch(() => '');
    throw new Error(
      errText || `Optimize description failed (${response.status})`
    );
  }
  return response.json();
};

const getCityObjects = async (
  lat: number,
  lon: number,
  radius: number = 500,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/city-objects?lat=${lat}&lon=${lon}&radius=${radius}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return response.json();
};

/**
 * A building query that carries the cubature: the building, its neighbours, and
 * `geometry3D` on each of them. The server loads whatever LoD2 tiles those
 * buildings reference and does the dequantising and projecting — doing it here
 * would be a second copy of logic that has already gone wrong once.
 */
const getBuildingsWith3D = async (
  buildingId: string,
  radius?: number,
  limit?: number,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/buildings/3d?buildingId=${encodeURIComponent(buildingId)}` +
      (radius ? `&radius=${radius}` : "") +
      (limit ? `&limit=${limit}` : ""),
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
  );

  return handleAuthenticatedResponse(response);
};

/**
 * Vegetation around a building for the 3D view (tri-server docs/vegetation-3d.md). Computed on
 * demand — the analysis takes minutes — so the view reads the stored state and starts it by button.
 */
const getVegetation = async (lat: number, lon: number, radius: number) => {
  const response = await authenticatedFetch(
    `${baseUrl}/city-objects/vegetation?lat=${lat}&lon=${lon}&radius=${radius}`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
  );
  return handleAuthenticatedResponse(response);
};

const startVegetation = async (lat: number, lon: number, radius: number, postalCode?: string) => {
  const response = await authenticatedFetch(`${baseUrl}/city-objects/vegetation`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ lat, lon, radius, postalCode }),
  });
  return handleAuthenticatedResponse(response);
};

/**
 * Sealed open ground (paths, yards, driveways, parking) around a point for the 3D view, from the latest stored
 * greenspace analysis covering it (tri-server docs/land-greenspace.md). Empty with `analysedAt: null` when none does.
 */
const getSealedSurfaces = async (lat: number, lon: number, radius: number) => {
  const response = await authenticatedFetch(
    `${baseUrl}/city-objects/sealed?lat=${lat}&lon=${lon}&radius=${radius}`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
  );
  return handleAuthenticatedResponse(response);
};

/**
 * Own 3D models of a building (IFC/GLB) for the LoD2 view (tri-server docs/building-models.md).
 * The server converts IFC to GLB on upload; the view loads the GLB via `getManagedFileContent`.
 */
const getBuildingModels = async (buildingId: string) => {
  const response = await authenticatedFetch(`${baseUrl}/buildings/${encodeURIComponent(buildingId)}/models`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return handleAuthenticatedResponse(response);
};

const uploadBuildingModel = async (buildingId: string, file: File) => {
  const body = new FormData();
  body.append("file", file, file.name);
  const response = await authenticatedFetch(`${baseUrl}/buildings/${encodeURIComponent(buildingId)}/models`, {
    method: "POST",
    body,
    signal: PATIENT_REQUEST,
  });
  return handleAuthenticatedResponse(response);
};

const updateBuildingModelPlacement = async (
  buildingId: string,
  modelId: string,
  placement: { lon: number; lat: number; altitude: number; rotationDeg: number },
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/buildings/${encodeURIComponent(buildingId)}/models/${encodeURIComponent(modelId)}/placement`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify(placement) },
  );
  return handleAuthenticatedResponse(response);
};

const setBuildingModelColourMode = async (buildingId: string, modelId: string, colourMode: "original" | "natural" | "house") => {
  const response = await authenticatedFetch(
    `${baseUrl}/buildings/${encodeURIComponent(buildingId)}/models/${encodeURIComponent(modelId)}/appearance`,
    { method: "PUT", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ colourMode }) },
  );
  return handleAuthenticatedResponse(response);
};

const deleteBuildingModel = async (buildingId: string, modelId: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/buildings/${encodeURIComponent(buildingId)}/models/${encodeURIComponent(modelId)}`,
    { method: "DELETE" },
  );
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
};

/** Volumenstudie eines Gebäudes: gezeichnete Baukörper und der Rahmen (Flurstück, zulässiges Maß). */
const getBuildingMassing = async (buildingId: string) => {
  const response = await authenticatedFetch(`${baseUrl}/buildings/${encodeURIComponent(buildingId)}/massing`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });
  return handleAuthenticatedResponse(response);
};

const saveBuildingMassing = async (buildingId: string, bodies: any[]) => {
  const response = await authenticatedFetch(`${baseUrl}/buildings/${encodeURIComponent(buildingId)}/massing`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ bodies }),
  });
  return handleAuthenticatedResponse(response);
};

const getBuildingMassingContext = async (buildingId: string, point?: { lat: number; lon: number } | null) => {
  const query = point ? `?lat=${point.lat}&lon=${point.lon}` : "";
  const response = await authenticatedFetch(`${baseUrl}/buildings/${encodeURIComponent(buildingId)}/massing/context${query}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
    signal: PATIENT_REQUEST,
  });
  return handleAuthenticatedResponse(response);
};

/** One company registered at a building (row of GET /customers/:customer/legal-persons/at-address). */
export interface LegalPersonAtAddress {
  _id: string;
  companyName?: string;
  tradeRegisterId?: string;
  legalForm?: string;
  purpose?: string;
  street?: string;
  streetNumber?: string;
  postalCode?: string;
  city?: string;
  phone?: string;
  email?: string;
  website?: string;
}

export interface LegalPersonsAtAddressQuery {
  /** Building centre; the server needs coordinates and/or `headquarter`. */
  lat?: number;
  lon?: number;
  /** Bounding box (all four or none); replaces the centre box on the server. */
  minLat?: number;
  maxLat?: number;
  minLon?: number;
  maxLon?: number;
  /** Building id (optional). */
  headquarter?: string;
  city?: string;
  postalCode?: string;
  street?: string;
  streetNumber?: string;
}

/**
 * Companies at a building (indexed read, tenant scoped by the JWT, at most 50 rows).
 * Replaces the unindexed LegalPerson address query (tri-server#1550).
 */
export const fetchLegalPersonsAtAddress = async (
  customer: string,
  query: LegalPersonsAtAddressQuery,
): Promise<LegalPersonAtAddress[]> => {
  const params = new URLSearchParams();
  (["lat", "lon", "minLat", "maxLat", "minLon", "maxLon", "headquarter", "city", "postalCode", "street", "streetNumber"] as const).forEach(
    (key) => {
      const value = query[key];
      if (value !== undefined && value !== null && String(value) !== "") {
        params.set(key, String(value));
      }
    },
  );

  const response = await authenticatedFetch(
    `${baseUrl}/customers/${encodeURIComponent(customer)}/legal-persons/at-address?${params.toString()}`,
    {
      method: "GET",
      headers: { "Content-Type": "application/json" },
    },
  );

  if (!response.ok) {
    if (response.status === 401) {
      throw new UnauthorizedError();
    }
    let message = `${response.status} ${response.statusText}`;
    try {
      const data = await response.json();
      message = data.message || data.error || message;
    } catch {
      // body is not JSON — keep the status line
    }
    throw new ApplicationError(message);
  }

  const result = await response.json();

  return Array.isArray(result) ? result : [];
};

const getCityObject = async (lat: number, lon: number) => {
  const response = await authenticatedFetch(
    `${baseUrl}/city-objects/single?lat=${lat}&lon=${lon}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return response.json();
};

const getDeviceTypeCatalog = async () => {
  const response = await authenticatedFetch(`${baseUrl}/iot/device-types`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

/**
 * Device configuration parameters — the ones that live on the device and travel
 * over the air, not the ones describing how this server reaches it.
 *
 * None of these three is synchronous with the device. A FireIQ device is asleep
 * between uplinks, so a write is queued and answered on the next one, which at
 * the default reporting interval is hours away. Callers have to present that as
 * pending rather than as done.
 */
const getDeviceConfiguration = async (gateway: string, device: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/gateways/${encodeURIComponent(gateway)}/devices/${encodeURIComponent(device)}/configuration`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
  );

  return response.json();
};

const updateDeviceConfiguration = async (
  gateway: string,
  device: string,
  parameters: Array<{ name: string; value: number | string }>,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/gateways/${encodeURIComponent(gateway)}/devices/${encodeURIComponent(device)}/configuration`,
    {
      method: "PUT",
      body: JSON.stringify({ parameters }),
      headers: { "Content-Type": "application/json" },
    },
  );

  const payload = await response.json();

  // The gateway validates and encodes before queueing, so a rejected value comes
  // back as a 400 with a reason. Surfacing it as an error is the whole point:
  // the alternative is a value that silently never reaches the device.
  if (!response.ok) throw new Error(payload?.error ?? `HTTP ${response.status}`);

  return payload;
};

const queryDeviceConfiguration = async (
  gateway: string,
  device: string,
  parameters?: string[],
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/gateways/${encodeURIComponent(gateway)}/devices/${encodeURIComponent(device)}/configuration/query`,
    {
      method: "POST",
      body: JSON.stringify(parameters ? { parameters } : {}),
      headers: { "Content-Type": "application/json" },
    },
  );

  const payload = await response.json();

  if (!response.ok) throw new Error(payload?.error ?? `HTTP ${response.status}`);

  return payload;
};

const submitSupportRequest = async (email: string, request: string) => {
  const response = await fetch(`${baseUrl}/support/requests`, {
    method: "POST",
    body: JSON.stringify({ email, request }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

const getRoles = async (customerID: string) => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/roles`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

const createRoleAssignment = async (
  customerID: string,
  user: string,
  role: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/role-assignments`,
    {
      method: "POST",
      body: JSON.stringify({ user, role }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  const data = await response.json();

  if (!response.ok) {
    throw new ApplicationError(data.error || response.statusText);
  }

  return data;
};

const deleteRoleAssignment = async (
  customerID: string,
  roleAssignment: RoleAssignment,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/role-assignments`,
    {
      method: "DELETE",
      body: JSON.stringify(roleAssignment),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  const data = await response.json();

  if (!response.ok) {
    throw new ApplicationError(data.error || response.statusText);
  }

  return data;
};

const createSite = async (customerID: string, site: Site) => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/sites`, {
    method: "POST",
    body: JSON.stringify(site),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

const updateSite = async (customerID: string, site: Site) => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/sites`, {
    method: "PUT",
    body: JSON.stringify(site),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

const deleteSite = async (customerID: string, site: Site) => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/sites`, {
    method: "DELETE",
    body: JSON.stringify(site),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

const createPartnership = async (
  customerID: string,
  partnership: Partnership,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/partnerships`,
    {
      method: "POST",
      body: JSON.stringify(partnership),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const updatePartnership = async (
  customerID: string,
  partnership: Partnership,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/partnerships`,
    {
      method: "PUT",
      body: JSON.stringify(partnership),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const deletePartnership = async (
  customerID: string,
  partnership: Partnership,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/partnerships`,
    {
      method: "DELETE",
      body: JSON.stringify(partnership),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const createRole = async (customerID: string, role: Role) => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/roles`, {
    method: "POST",
    body: JSON.stringify(role),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

const deleteRole = async (customerID: string, role: Role) => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/roles`, {
    method: "DELETE",
    body: JSON.stringify(role),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

const updateAsset = async (asset: Asset) => {
  const response = await authenticatedFetch(`${baseUrl}/assets`, {
    method: "PUT",
    body: JSON.stringify(asset),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

const getProcessDefinitions = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/process-definitions`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const getChecklists = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/checklists`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const startChecklist = async (customerID: string, checklistId: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/checklists/${checklistId}/start`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleAuthenticatedResponse(response, (data) => data);
};

const getChecklistInstances = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/checklist-instances`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const createChecklist = async (customerID: string, checklist: Checklist) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/checklists`,
    {
      method: "POST",
      body: JSON.stringify(checklist),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const updateChecklist = async (customerID: string, checklist: Checklist) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/checklists/${checklist._id}`,
    {
      method: "PUT",
      body: JSON.stringify(checklist),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const deleteChecklist = async (customerID: string, checklist: Checklist) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/checklists/${checklist._id}`,
    {
      method: "DELETE",
      body: JSON.stringify(checklist),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

/**
 * BPM user task ↔ checklist instance binding. The instance is created on
 * first claim and resumed on subsequent claims of the same workItemId.
 */
const startWorkItemChecklistInstance = async (
  customerID: string,
  workItemId: string,
): Promise<any> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/work-items/${encodeURIComponent(workItemId)}/checklist-instance`,
    { method: "POST" },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error((err as { error?: string }).error ?? `Start failed: ${response.status}`);
  }
  return await response.json();
};

const getProcessInstanceChecklistInstances = async (
  customerID: string,
  processInstanceId: string,
): Promise<any[]> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/process-instances/${encodeURIComponent(processInstanceId)}/checklist-instances`,
    { method: "GET" },
  );
  return handleAuthenticatedResponse(
    response,
    (data: any) => (Array.isArray(data) ? data : []),
  );
};

const updateWorkItemChecklistInstance = async (
  customerID: string,
  workItemId: string,
  patch: { fields?: any[]; data?: any },
): Promise<any> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/work-items/${encodeURIComponent(workItemId)}/checklist-instance`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(patch),
    },
  );
  if (!response.ok) {
    const err = await response.json().catch(() => ({ error: response.statusText }));
    throw new Error((err as { error?: string }).error ?? `Patch failed: ${response.status}`);
  }
  return await response.json();
};

const getTasks = async (customerID: string) => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/tasks`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  return (await response.json()).map((task: any) => new Task(task));
};

const getOpenTasks = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/tasks/open`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return (await response.json()).map((task: any) => new Task(task));
};

const activateTask = async (customerID: string, task: Task) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/tasks/${task._id}/activate`,
    {
      method: "POST",
      body: JSON.stringify(task),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const deactivateTask = async (customerID: string, task: Task) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/tasks/${task._id}/deactivate`,
    {
      method: "POST",
      body: JSON.stringify(task),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const deactivateAndSaveTask = async (customerID: string, task: Task) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/tasks/${task._id}/deactivate-and-save`,
    {
      method: "POST",
      body: JSON.stringify(task),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const completeTask = async (
  customerID: string,
  task: Task,
  userInput?: string,
) => {
  const body: any = { ...task };
  if (userInput?.trim()) {
    body.userInput = userInput.trim();
  }
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/tasks/${task._id}/complete`,
    {
      method: "POST",
      body: JSON.stringify(body),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const abortTask = async (customerID: string, task: Task) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/tasks/${task._id}/abort`,
    {
      method: "POST",
      body: JSON.stringify(task),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const taskStatistics = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/tasks/statistics`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const fullOrderForProject = async (customerId: string, projectID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/customers/${customerId}/projects/${projectID}/service-orders`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  if (response.ok) {
    const result = await response.json();

    if (result) {
      return new Order(result);
    } else {
      return null;
    }
  } else {
    if (response.status === 400) {
      throw new ApplicationError((await response.json()).message);
    } else if (response?.status === 401) {
      throw new UnauthorizedError();
    }
  }
};

const getConsumerOrders = async (customerId: string, signal?: AbortSignal) => {
  const response = await authenticatedFetch(
    `${baseUrl}/customers/${customerId}/consumer-orders`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // Endet mit der Ansicht: siehe `abortedRequests`.
      signal,
    },
  );

  return handleResponseArray(response, (entry: any) => {
    return new Order(entry);
  });
};

const getProviderOrders = async (customerId: string, signal?: AbortSignal) => {
  const response = await authenticatedFetch(
    `${baseUrl}/customers/${customerId}/provider-orders`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // Endet mit der Ansicht: siehe `abortedRequests`.
      signal,
    },
  );

  return handleResponseArray(response, (entry: any) => {
    return new Order(entry);
  });
};

// Billing address structure matching LegalPerson + Address models
interface BillingInfo {
  company: string;
  street: string;
  streetNumber: string;
  postalCode: string;
  city: string;
  country: string;
  taxId?: string;
  tradeRegisterId?: string;
  tradeRegisterCity?: string;
}

interface BillingInfoResponse {
  hasLegalPerson: boolean;
  billingInfo: BillingInfo | null;
  customerName?: string;
}

/**
 * Get customer billing info (LegalPerson data)
 */
const getCustomerBillingInfo = async (
  customerId: string,
): Promise<BillingInfoResponse> => {
  try {
    const response = await authenticatedFetch(
      `${baseUrl}/api/customers/${customerId}/billing-info`,
      {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    if (!response.ok) {
      console.error("Error fetching billing info:", response.status);
      return { hasLegalPerson: false, billingInfo: null };
    }

    return await response.json();
  } catch (error) {
    console.error("Error fetching billing info:", error);
    return { hasLegalPerson: false, billingInfo: null };
  }
};

/**
 * Update customer billing info (create/update LegalPerson)
 */
const updateCustomerBillingInfo = async (
  customerId: string,
  billingData: BillingInfo,
): Promise<{ success: boolean; error?: string }> => {
  try {
    const response = await authenticatedFetch(
      `${baseUrl}/api/customers/${customerId}/billing-info`,
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(billingData),
      },
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || "Failed to update billing info",
      };
    }

    return { success: true };
  } catch (error) {
    console.error("Error updating billing info:", error);
    return { success: false, error: "Failed to update billing info" };
  }
};

const getInvoiceForecastForProject = async (
  customerId: string,
  projectID: string,
  from: Date,
  to: Date,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/customers/${customerId}/projects/${projectID}/invoices/forecast?from=${from.toISOString()}&to=${to.toISOString()}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return handleResponseArray(response, (entry: any) => {
    return new Invoice(entry);
  });
};

const runBillingForProject = async (customerID: string, projectID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/billing/projects/${projectID}`,
    {
      method: "POST",
      body: JSON.stringify({}),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const createMissingInvoicesForProject = async (
  customerID: string,
  projectID: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/customers/${customerID}/projects/${projectID}/invoices`,
    {
      method: "POST",
      body: JSON.stringify({}),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const sampleDXF = async () => {
  const response = await authenticatedFetch(`${baseUrl}/sample-dxf`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  return response.json();
};

const uploadFile = async (form: any) => {
  const response = await authenticatedFetch(`${baseUrl}/file`, {
    method: "PUT",
    body: form,
    headers: {
      // Let the browser set the correct Content-Type with boundary for FormData
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return response.json();
};

const uploadDocument = async (
  customerID: string,
  file: File,
  title: string,
  documentType: DocumentType,
) => {
  const form = new FormData();

  form.append("file", file);

  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/document-details/${title}/${documentType}`,
    {
      method: "PUT",
      body: form,
      headers: {
        // Let the browser set the correct Content-Type with boundary for FormData
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return new DocumentDetails(result);
  });
};

const deleteDocument = async (
  customerID: string,
  document: DocumentDetails,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/document-details`,
    {
      method: "DELETE",
      body: JSON.stringify(document),
      headers: {
        "Content-Type": "application/json",
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return handleResponse(response, (_result: any) => {
    return document;
  });
};

const classifyImageDIN276 = async (customerID: string, file: File) => {
  const form = new FormData();

  form.append("image", file);

  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/image-classification/din276`,
    {
      method: "POST",
      body: form,
      headers: {
        // Let the browser set the correct Content-Type with boundary for FormData
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return result;
  });
};

const assetPhotoDummy = async (customerID: string, file: File) => {
  const form = new FormData();

  form.append("assetPhoto", file);

  const response = await authenticatedFetch(`${baseUrl}/assets/photo`, {
    method: "PUT",
    body: form,
    headers: {
      // Let the browser set the correct Content-Type with boundary for FormData
      // "authorization": "Bearer " + authentication.access_token
    },
  });

  return handleResponse(response, (result: any) => {
    return result;
  });
};

const getConversations = async (
  customerID: string,
  signal?: AbortSignal,
): Promise<Conversation[]> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations`,
    {
      method: "GET",
      signal,
    },
  );

  return handleAuthenticatedResponse(response, (conversations: any[]) => {
    return conversations.map(
      (conversation: any) => new Conversation(conversation),
    );
  });
};

/** Lightweight list for display only. Fetch full via getConversation when user selects. */
const getConversationList = async (
  customerID: string,
  signal?: AbortSignal,
  page?: { limit: number; offset: number },
): Promise<any[]> => {
  // Without `page` the route answers the whole list (legacy); with it, one newest-first slice.
  const query = page ? `?limit=${page.limit}&offset=${page.offset}` : "";
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/list${query}`,
    {
      method: "GET",
      signal,
    },
  );

  return handleAuthenticatedResponse(response, (data: any) => {
    return Array.isArray(data) ? data : [];
  });
};

const getConversation = async (
  customerID: string,
  conversationID: string,
  signal?: AbortSignal,
): Promise<Conversation> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}`,
    {
      method: "GET",
      signal,
    },
  );

  return handleAuthenticatedResponse(response, (conversation: any) => {
    return new Conversation(conversation);
  });
};

/**
 * Is a turn (chat answer or process step) running in this conversation on the server? Asked after
 * loading a conversation, so a reloaded tab can wait for a turn it has no stream for.
 */
const getConversationRun = async (
  customerID: string,
  conversationID: string,
  signal?: AbortSignal,
): Promise<{ running: boolean; kind?: string; since?: string }> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/run`,
    { method: "GET", signal },
  );
  return handleAuthenticatedResponse(response, (state: any) => ({
    running: state?.running === true,
    kind: state?.kind,
    since: state?.since,
  }));
};

const createConversation = async (
  customerID: string,
  options?: { toolId?: string },
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations`,
    {
      method: "POST",
      ...(options?.toolId
        ? {
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify({ toolId: options.toolId }),
          }
        : {}),
    },
  );

  return handleAuthenticatedResponse(response, (result: any) => {
    return new Conversation(result);
  });
};

const deleteConversation = async (
  customerID: string,
  conversation: Conversation,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations`,
    {
      method: "DELETE",
      body: JSON.stringify(conversation),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

export interface ContextDocumentJobResponse {
  jobId: string;
  statusUrl?: string;
  files?: any[];
  /**
   * Names the server did not attach — an unsupported type, an id that did not resolve, or a file
   * whose processing could not be started. Only the from-storage endpoint returns this.
   */
  skipped?: string[];
  /** The same entries with the reason each was left out, for a message in the user's language. */
  skippedDetails?: Array<{ name: string; reason: string }>;
}

export interface ContextDocumentJobStatus {
  jobId: string;
  ragStatus: string;
  jobStatus: "pending" | "processing" | "finalizing" | "completed" | "failed";
  progress?: {
    completed: number | null;
    total: number | null;
    detail: string | null;
  };
  error?: string | null;
  conversation?: any;
}

const uploadContextDocuments = async (
  customerID: string,
  conversationID: string,
  files: File[],
): Promise<ContextDocumentJobResponse> => {
  const form = new FormData();

  files.forEach((file) => {
    form.append("files", file);
  });

  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/context-documents`,
    {
      method: "POST",
      body: form,
      headers: {
        // Let the browser set the correct Content-Type with boundary for FormData
      },
    },
  );

  const jobResponse = await handleResponse(
    response,
    (result: any) => result as ContextDocumentJobResponse,
  );

  if (!jobResponse) {
    throw new Error("Context document upload did not return a job reference");
  }

  return jobResponse;
};

const uploadSingleContextDocument = async (
  customerID: string,
  conversationID: string,
  file: File,
): Promise<ContextDocumentJobResponse> => {
  const form = new FormData();
  form.append("files", file, file.name.replace(/[^a-zA-Z0-9._\- ]/g, ""));

  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/context-documents`,
    {
      method: "POST",
      body: form,
      headers: {
        // Let the browser set the correct Content-Type with boundary for FormData
      },
    },
  );

  // The server answers 413 when the file exceeds its upload limit. This is the
  // authoritative check — the caller's pre-flight one can be stale if the limit
  // changed mid-session. Handle it here because handleResponse only maps 400/401
  // and would otherwise return undefined, surfacing as "no job reference".
  if (response.status === 413) {
    const body = await response.json().catch(() => ({}) as any);

    throw new FileTooLargeError(body.maxFileSizeBytes);
  }

  const jobResponse = await handleResponse(
    response,
    (result: any) => result as ContextDocumentJobResponse,
  );

  if (!jobResponse) {
    throw new Error("Context document upload did not return a job reference");
  }

  return jobResponse;
};

/**
 * Attach files the customer already has in the managed file system, picked with the file picker
 * instead of uploaded. No bytes are sent: the server resolves the ids (and walks the picked
 * folders), reuses the markdown and chunks of anything already processed, and returns the same job
 * reference the upload path does — so the caller polls it identically.
 */
const attachStorageContextDocuments = async (
  customerID: string,
  conversationID: string,
  // Each folder carries the volume it was browsed in; the server walks it against that volume.
  picked: { fileIds: string[]; folderPaths: Array<{ path: string; volume: string }> },
): Promise<ContextDocumentJobResponse> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/context-documents/from-storage`,
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(picked),
    },
  );

  // Every refusal this endpoint makes carries a message worth showing: too many files (400),
  // nothing attachable (400), or the processing service being down (503). handleResponse only maps
  // 400/401 and would otherwise return undefined, surfacing as a useless "no job reference".
  if (!response.ok && response.status !== 401) {
    const body = await response.json().catch(() => ({}) as any);

    // `code`, `maxFiles` and `skippedDetails` ride along so the caller can render the refusal in
    // the user's language instead of the server's English `message`, which is the fallback when it
    // cannot.
    throw Object.assign(
      new Error(body.message || body.error || "Files could not be attached"),
      { code: body.code, maxFiles: body.maxFiles, skippedDetails: body.skippedDetails },
    );
  }

  const jobResponse = await handleResponse(
    response,
    (result: any) => result as ContextDocumentJobResponse,
  );

  if (!jobResponse) {
    throw new Error("Context document attach did not return a job reference");
  }

  return jobResponse;
};

/** One attached file, shaped like a context document so the document chips render it. */
export interface ConversationFile {
  _id: string;
  filename: string;
  mimeType?: string;
  summary?: string;
  fileId?: string;
  webUrl?: string;
}

/**
 * The files of a conversation, for views other than the chat (the tender board's „Dateien").
 * Readable by whoever may read the conversation; anyone else gets 403, which the thrown error
 * carries as `status` so the caller can say what it means.
 */
const getConversationFiles = async (
  customerID: string,
  conversationID: string,
): Promise<ConversationFile[]> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/files`,
    { method: "GET", headers: { "Content-Type": "application/json" } },
  );

  if (!response.ok && response.status !== 401) {
    throw Object.assign(new Error(`Failed to load conversation files: ${response.status}`), {
      status: response.status,
    });
  }

  // `handleResponse` is typed off a `void` callback, so its result has to be typed here.
  const result = (await handleResponse(response, (body: any) => body)) as
    | { files?: ConversationFile[] }
    | null
    | undefined;
  const files = result?.files;
  return Array.isArray(files) ? files : [];
};

/**
 * Add one file to a conversation — the chat's upload pipeline behind an access check. The server
 * finishes the job itself, so polling `getContextDocumentJobStatus` is for showing progress only;
 * the file is attached even if the page is gone by then.
 */
const uploadConversationFile = async (
  customerID: string,
  conversationID: string,
  file: File,
): Promise<ContextDocumentJobResponse> => {
  const form = new FormData();
  // The name as the user sees it: the server reads the multipart filename as UTF-8
  // (tri-server docs/upload-filename-charset.md), so umlauts survive.
  form.append("files", file, file.name);

  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/files`,
    {
      method: "POST",
      body: form,
      headers: {
        // Let the browser set the correct Content-Type with boundary for FormData
      },
    },
  );

  if (response.status === 413) {
    const body = await response.json().catch(() => ({}) as any);

    throw new FileTooLargeError(body.maxFileSizeBytes);
  }

  if (!response.ok && response.status !== 401) {
    const body = await response.json().catch(() => ({}) as any);

    throw Object.assign(new Error(body.message || body.error || `Upload failed: ${response.status}`), {
      status: response.status,
    });
  }

  // `handleResponse` is typed off a `void` callback, so its result has to be typed here.
  const jobResponse = (await handleResponse(response, (result: any) => result)) as
    | ContextDocumentJobResponse
    | null
    | undefined;

  if (!jobResponse?.jobId) {
    throw new Error("Upload did not return a job reference");
  }

  return jobResponse;
};

const getContextDocumentJobStatus = async (
  customerID: string,
  conversationID: string,
  jobId: string,
): Promise<ContextDocumentJobStatus> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/context-documents/jobs/${jobId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  const jobStatus = await handleResponse(
    response,
    (result: any) => result as ContextDocumentJobStatus,
  );

  if (!jobStatus) {
    throw new Error("Job status response was empty");
  }

  return jobStatus;
};

const deleteContextDocument = async (
  customerID: string,
  conversationID: string,
  documentID: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/context-documents/${documentID}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return new Conversation(result);
  });
};

/**
 * Fetch document content by fileId (ManagedFile ID). Use for original or markdown.
 * Independent of conversation - works for any file the user has access to.
 */
const getDocumentByFileId = async (
  customerID: string,
  fileId: string,
  type: "download" | "markdown",
  fallbackFilename?: string,
): Promise<{ blob: Blob; filename: string; mimeType: string }> => {
  const path = type === "download" ? "download" : "markdown";
  const url = `${baseUrl}/${customerID}/documents/${fileId}/${path}`;
  const response = await authenticatedFetch(url, { method: "GET", signal: PATIENT_REQUEST });
  if (!response.ok) {
    throw new Error(`Failed to fetch document: ${response.status}`);
  }
  const blob = await response.blob();
  const mimeType =
    response.headers.get("Content-Type") ||
    (type === "markdown" ? "text/markdown" : "application/octet-stream");
  const contentDisposition = response.headers.get("Content-Disposition");
  let filename = fallbackFilename || "document";
  if (contentDisposition) {
    const match = contentDisposition.match(
      /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
    );
    if (match) {
      filename = decodeURIComponent(match[1].replace(/['"]/g, "").trim());
    }
  } else if (fallbackFilename && type === "markdown") {
    filename = fallbackFilename.toLowerCase().endsWith(".md")
      ? fallbackFilename
      : `${fallbackFilename.replace(/\.[^.]+$/, "")}.md`;
  }
  return { blob, filename, mimeType };
};

/**
 * Die Bytes einer verwalteten Datei für den Dokumentbetrachter — inline, mit Prüfung von Mandant
 * **und** Volume-Rollen (tri-server `GET /:customer/managed-files/:fileId/content`, siehe dort
 * docs/document-viewer.md). `status` trägt 403/404 nach außen, damit der Betrachter sagen kann,
 * warum nichts kommt, statt nur „Fehler".
 */
const getManagedFileContent = async (
  customerID: string,
  fileId: string,
): Promise<{ data: ArrayBuffer; mimeType: string } | { status: number }> => {
  const url = `${baseUrl}/${customerID}/managed-files/${encodeURIComponent(fileId)}/content`;
  const response = await authenticatedFetch(url, { method: "GET", signal: PATIENT_REQUEST });
  if (!response.ok) {
    return { status: response.status };
  }
  return {
    data: await response.arrayBuffer(),
    mimeType: (response.headers.get("Content-Type") || "application/octet-stream").split(";")[0].trim(),
  };
};

/**
 * Ein signierter Link auf eine Datei unter `/documents` — für Leser ohne Header und ohne
 * Datei-Session-Cookie, allen voran PowerPoint hinter `ms-powerpoint:ofe|u|…`. Signiert wird nur,
 * was der angemeldete Nutzer selbst lesen darf; der Link gilt 30 Tage
 * (tri-server docs/documents-route-access.md).
 */
const signFileLink = async (url: string): Promise<string> => {
  const response = await authenticatedFetch(`${baseUrl}/auth/file-link`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ url }),
  });
  if (!response.ok) {
    throw new Error(`file link ${response.status}`);
  }
  return (await response.json()).url;
};

const getContextDocumentDownload = async (
  customerID: string,
  conversationID: string,
  documentID: string,
  fallbackFilename?: string,
): Promise<{ blob: Blob; filename: string; mimeType: string }> => {
  const tryDownload = async (requestUrl: string) => {
    const response = await authenticatedFetch(requestUrl, { method: "GET", signal: PATIENT_REQUEST });
    if (!response.ok) return null;
    const blob = await response.blob();
    const mimeType =
      response.headers.get("Content-Type") || "application/octet-stream";
    const contentDisposition = response.headers.get("Content-Disposition");
    let filename = fallbackFilename || "document";
    if (contentDisposition) {
      const match = contentDisposition.match(
        /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/,
      );
      if (match) {
        filename = decodeURIComponent(match[1].replace(/['"]/g, "").trim());
      }
    } else if (fallbackFilename) {
      filename = fallbackFilename;
    }
    return { blob, filename, mimeType };
  };

  const basePath = `${baseUrl}/${customerID}/conversations/${conversationID}/context-documents/${documentID}`;
  let result = await tryDownload(`${basePath}/download`);
  if (!result) {
    result = await tryDownload(basePath);
  }
  if (!result) {
    throw new Error("Failed to download context document");
  }
  return result;
};

const interruptConversation = async (
  customerID: string,
  conversationID: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/interrupt`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleAuthenticatedResponse(response, (result: any) => {
    return new Conversation(result);
  });
};

export type TaskSelectOption = { value: string | number; label: string };

/**
 * Options of a `tri:uxElement="select"` user task, loaded live by the server from the tool the
 * BPMN names (e.g. Odoo companies). Throws an ApplicationError carrying the server's message.
 */
const getConversationTaskSelectOptions = async (
  customerID: string,
  conversationID: string,
  taskID: string,
): Promise<TaskSelectOption[]> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/tasks/${taskID}/select-options`,
    { method: "GET" },
  );
  if (response.status === 401) {
    throw new UnauthorizedError();
  }
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApplicationError(
      body?.message ?? body?.error ?? `Loading options failed: ${response.status}`,
    );
  }
  return Array.isArray(body?.options) ? body.options : [];
};

export type TaskFormOption = {
  value: string | number;
  label: string;
  onlyWhen?: { field: string; values: Array<string | number> };
};
export type TaskFormField = {
  key: string;
  label: string;
  type: 'text' | 'textarea' | 'number' | 'date' | 'select' | 'multiselect';
  required?: boolean;
  options?: TaskFormOption[];
  allowNew?: boolean;
  newValueKey?: string;
  value?: unknown;
  newValue?: string;
  hint?: string;
};
export type TaskForm = { fields: TaskFormField[] };

/** The form of a `tri:uxElement="form"` user task, built once per task by the server. */
const getConversationTaskForm = async (
  customerID: string,
  conversationID: string,
  taskID: string,
): Promise<TaskForm> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/conversations/${conversationID}/tasks/${taskID}/form`,
    { method: "GET" },
  );
  if (response.status === 401) {
    throw new UnauthorizedError();
  }
  const body = await response.json().catch(() => null);
  if (!response.ok) {
    throw new ApplicationError(
      body?.message ?? body?.error ?? `Loading form failed: ${response.status}`,
    );
  }
  return { fields: Array.isArray(body?.form?.fields) ? body.form.fields : [] };
};

/**
 * A proxy in front of the server gave up waiting (502/503/504) — the server itself did not answer,
 * so this says nothing about whether the task was completed.
 */
export const isGatewayGiveUp = (status: number): boolean =>
  status === 502 || status === 503 || status === 504;

const completeConversationTask = async (
  customerID: string,
  conversationID: string,
  taskID: string,
  comment: string,
  botMessage?: { content: string; confirmation?: boolean; resources?: any[] },
  selection?: { value: string | number },
  form?: { values: Record<string, unknown> },
): Promise<Conversation | null> => {
  const body: {
    comment: string;
    botMessage?: { content: string; confirmation?: boolean; resources?: any[] };
    selection?: { value: string | number };
    form?: { values: Record<string, unknown> };
  } = { comment };
  if (botMessage?.content) {
    body.botMessage = botMessage;
  }
  // A `select` task: the server reloads the options and takes value and label from there.
  if (selection) {
    body.selection = { value: selection.value };
  }
  // A `form` task: the server validates against the form it stored for this task.
  if (form) {
    body.form = { values: form.values };
  }
  // The server runs the process on to the next user task inside this request — a service task
  // such as creating the Odoo lead takes about a minute (Prod, 01.10.2026). The default 45 s
  // deadline aborted it while the server carried on, and the user saw "communication problems"
  // over a lead that was created. So no deadline here; a connection that is lost anyway (network,
  // or a gateway giving up) is a TaskCompletionInterruptedError, and the caller follows the run.
  let response: Response;
  try {
    response = await authenticatedFetch(
      `${baseUrl}/${customerID}/conversations/${conversationID}/tasks/${taskID}/complete`,
      {
        method: "POST",
        body: JSON.stringify(body),
        headers: {
          "Content-Type": "application/json",
        },
        signal: PATIENT_REQUEST,
      },
    );
  } catch (err) {
    if (err instanceof UnauthorizedError) throw err;
    throw new TaskCompletionInterruptedError(err);
  }

  if (isGatewayGiveUp(response.status)) {
    throw new TaskCompletionInterruptedError(response.status);
  }

  if (!response.ok) {
    let message = `Task completion failed: ${response.status}`;
    try {
      const text = await response.text();
      if (text) {
        const body = JSON.parse(text);
        if (body?.message) message = body.message;
        else message = text;
      }
    } catch {
      /* use default message */
    }
    if (response.status === 400) {
      throw new ApplicationError(message);
    }
    if (response.status === 401) {
      throw new UnauthorizedError();
    }
    if (response.status === 403) {
      throw new ApplicationError(
        message ||
          "Access denied. You may not have permission to complete this task.",
      );
    }
    throw new ApplicationError(message);
  }

  const result = await response.json();
  if (result == null) {
    return null;
  }
  const conv = new Conversation(result);
  // Preserve generic tri:* attrs map and the legacy userPromptMap alias
  // (the Conversation model strips unknown properties).
  if (result.triAttrsByNodeId) (conv as any).triAttrsByNodeId = result.triAttrsByNodeId;
  if (result.userPromptMap) (conv as any).userPromptMap = result.userPromptMap;
  return conv;
};

const getResource = async (
  customerID: string,
  resourceID: string,
): Promise<any> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/resources/${resourceID}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (entry: any) => {
    return new Resource(entry);
  });
};

const createMarketplaceOffer = async (
  customerID: string,
  marketplaceOffer: MarketplaceOffer,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers`,
    {
      method: "POST",
      body: JSON.stringify(marketplaceOffer),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    // @ts-ignore
    return new (getEntityType(marketplaceOffer._type).type.type)(result);
  });
};

const updateMarketplaceOffer = async (
  customerID: string,
  marketplaceOffer: MarketplaceOffer,
  signal?: AbortSignal,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}`,
    {
      method: "PUT",
      body: JSON.stringify(marketplaceOffer),
      headers: {
        "Content-Type": "application/json",
      },
      signal,
    },
  );

  return handleResponse(response, (result: any) => {
    // Ensure all fields from result are copied back to the instance (like bpmnXML)
    // @ts-ignore
    const instance = new (getEntityType(marketplaceOffer._type).type.type)(
      result,
    );
    Object.assign(instance, result);
    return instance;
  });
};

const deleteMarketplaceOffer = async (
  customerID: string,
  marketplaceOffer: MarketplaceOffer,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}`,
    {
      method: "DELETE",
      body: JSON.stringify(marketplaceOffer),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const submitMarketplaceOffer = async (
  customerID: string,
  marketplaceOffer: MarketplaceOffer,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}/submit`,
    {
      method: "PATCH",
      body: JSON.stringify(marketplaceOffer),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    console.log("submitMarketplaceOffer", result);
    // @ts-ignore
    return new (getEntityType(marketplaceOffer._type).type.type)(result);
  });
};

const publishMarketplaceOffer = async (
  customerID: string,
  marketplaceOffer: MarketplaceOffer,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}/publish`,
    {
      method: "PATCH",
      body: JSON.stringify(marketplaceOffer),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    // @ts-ignore
    return new (getEntityType(marketplaceOffer._type).type.type)(result);
  });
};

const unpublishMarketplaceOffer = async (
  customerID: string,
  marketplaceOffer: MarketplaceOffer,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}/unpublish`,
    {
      method: "PATCH",
      body: JSON.stringify(marketplaceOffer),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    // @ts-ignore
    return new (getEntityType(marketplaceOffer._type).type.type)(result);
  });
};

const appealAfterOfferRejection = async (
  customerID: string,
  marketplaceOffer: MarketplaceOffer,
  appealReason: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}/appeal`,
    {
      method: "PATCH",
      body: JSON.stringify({
        offer: marketplaceOffer,
        appealReason: appealReason,
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    // @ts-ignore
    return new (getEntityType(marketplaceOffer._type).type.type)(result);
  });
};

const refreshMarketplaceOffer = async (
  customerID: string,
  marketplaceOffer: MarketplaceOffer,
) => {
  if (!marketplaceOffer._id) {
    throw new Error("Marketplace offer ID is required for refresh");
  }

  const url = `${baseUrl}/${customerID}/marketplace-offers/${marketplaceOffer._id}`;
  console.log(`📡 refreshMarketplaceOffer calling: ${url}`);

  let response = await authenticatedFetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  console.log(`📡 refreshMarketplaceOffer response status: ${response.status}`);

  if (response.status === 404) {
    const fallbackUrl = `${baseUrl}/marketplace-offers/${marketplaceOffer._id}`;
    console.log(
      `📡 refreshMarketplaceOffer 404, trying fallback: ${fallbackUrl}`,
    );
    response = await authenticatedFetch(fallbackUrl, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    console.log(
      `📡 refreshMarketplaceOffer fallback status: ${response.status}`,
    );
  }

  if (response.status === 404) {
    const entityTypeName = marketplaceOffer._type || "MarketplaceOffer";
    const fallbackUrl2 = `${baseUrl}/entities/${entityTypeName}/${marketplaceOffer._id}`;
    console.log(
      `📡 refreshMarketplaceOffer 404, trying second fallback: ${fallbackUrl2}`,
    );
    response = await authenticatedFetch(fallbackUrl2, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    });
    console.log(
      `📡 refreshMarketplaceOffer second fallback status: ${response.status}`,
    );
  }

  return handleResponse(response, (result: any) => {
    console.log(
      `✅ refreshMarketplaceOffer success. bpmnXML length: ${result?.bpmnXML?.length || 0}`,
    );
    // Create instance and ensure all fields from result are copied (like bpmnXML)
    // @ts-ignore
    const instance = new (getEntityType(marketplaceOffer._type).type.type)(
      result,
    );
    Object.assign(instance, result);
    return instance;
  });
};

const getCustomerDetails = async (customerId: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/customers/${customerId}/details`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    if (response.status === 404) {
      throw new Error("Customer not found");
    }
    throw new Error(`Failed to fetch customer details: ${response.statusText}`);
  }

  return response.json();
};

const createMarketplaceOfferVoucher = async (
  customerID: string,
  offerId: string,
  targetCustomerIdOrEmail: string,
  isEmail: boolean = false,
  plan?: any, // The plan to include in the voucher
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${offerId}/voucher`,
    {
      method: "POST",
      body: JSON.stringify({
        customer: targetCustomerIdOrEmail,
        email: isEmail ? targetCustomerIdOrEmail : undefined,
        plan: plan, // Include the full plan in the voucher JWT
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    // An ApplicationError is shown with its text (pushError) — a plain Error only as "Es ist ein Fehler
    // aufgetreten", which hid whether the server or the clipboard failed.
    let detail = response.statusText;
    try {
      const body = await response.json();
      detail = body?.error || body?.message || detail;
    } catch {
      // body not JSON — keep the status text
    }
    throw new ApplicationError(`Gutschein konnte nicht erstellt werden (${response.status}${detail ? `: ${detail}` : ""})`);
  }

  // The response might be a JWT token string directly, or JSON with a voucher field
  const contentType = response.headers.get("content-type");
  if (contentType && contentType.includes("application/json")) {
    const result = await response.json();
    return result.voucher || result;
  } else {
    // If not JSON, assume it's the JWT token string directly
    return await response.text();
  }
};

const sendMarketplaceOfferVoucherEmail = async (
  customerID: string,
  offerId: string,
  targetCustomerIdOrEmail: string,
  isEmail: boolean = false,
  plan?: any, // The plan to include in the voucher
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${offerId}/voucher/email`,
    {
      method: "POST",
      body: JSON.stringify({
        customer: targetCustomerIdOrEmail,
        email: isEmail ? targetCustomerIdOrEmail : undefined,
        plan: plan, // Include the full plan in the voucher JWT
      }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to send voucher email: ${response.statusText}`);
  }

  return response.json();
};

/**
 * Validate a voucher code and get the custom plan
 * This is a public endpoint that doesn't require authentication
 * The offer ID is extracted from the voucher JWT, no need to pass it separately
 * @param voucher - The voucher JWT token
 * @returns The custom plan and any additional info (requiresRegistration, requiresLogin, email, offerId, offerName)
 */
const validateVoucher = async (
  voucher: string,
): Promise<{
  plan?: any;
  offerId?: string;
  offerName?: string;
  requiresRegistration?: boolean;
  requiresLogin?: boolean;
  email?: string;
  customer?: string;
  error?: string;
}> => {
  const response = await fetch(
    `${baseUrl}/voucher/validate?voucher=${encodeURIComponent(voucher)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.error || `Failed to validate voucher: ${response.statusText}`,
    );
  }

  return response.json();
};

const updateKnowledgeBaseImages = async (
  customerID: string,
  knowledgeBase: KnowledgeBase,
  form: any,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/knowledge-bases/${knowledgeBase._id}/images`,
    {
      method: "PUT",
      body: form,
      headers: {
        // Let the browser set the correct Content-Type with boundary for FormData
        // "authorization": "Bearer " + authentication.access_token
      },
    },
  );

  return response.json();
};

const getMarketplaceOffers = async (customerID: string, signal?: AbortSignal): Promise<any[]> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // Endet mit der Ansicht: siehe `abortedRequests`.
      signal,
    },
  );

  return await handleResponseArray(response, (entry: any) => {
    // @ts-ignore
    const instance = new (getEntityType(entry._type).type.type)(entry);
    Object.assign(instance, entry);
    return instance;
  });
};

/** Get tool configuration for an offer (tenant-scoped). Returns null if 404. */
const getToolConfiguration = async (
  customerID: string,
  offerId: string,
): Promise<{ schemaConfiguration: Record<string, string> } | null> => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${offerId}/tool-configuration`,
    { method: "GET" },
  );
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(await response.text());
  const result = await response.json();
  return result;
};

/** Upsert tool configuration (create or update). */
const putToolConfiguration = async (
  customerID: string,
  offerId: string,
  schemaConfiguration: Record<string, string>,
  configurationSchema?: Array<{ id?: string; type?: string }>,
): Promise<any> => {
  const passwordFieldIds = getPasswordFieldIdSet(configurationSchema);
  const payload = omitMaskedToolConfigurationValues(
    schemaConfiguration,
    passwordFieldIds,
  );
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace-offers/${offerId}/tool-configuration`,
    {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ schemaConfiguration: payload }),
    },
  );
  if (!response.ok) throw new Error(await response.text());
  return response.json();
};

/** Fetch a single marketplace offer by ID (public/consumer endpoint). */
const getMarketplaceOfferById = async (
  offerId: string,
): Promise<any | null> => {
  try {
    const response = await authenticatedFetch(
      `${baseUrl}/marketplace-offers/${offerId}`,
      { method: "GET" },
    );
    if (!response.ok) return null;
    return response.json();
  } catch {
    return null;
  }
};

const getPublicMarketplaceOffers = async (): Promise<any[]> => {
  const response = await fetch(`${baseUrl}/marketplace-offers`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  return await handleResponseArray(response, (entry: any) => {
    // @ts-ignore
    return new (getEntityType(entry._type).type.type)(entry);
  });
};

const getFeaturedMarketplaceOffers = async (): Promise<any[]> => {
  const response = await fetch(`${baseUrl}/marketplace-offers/featured`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  return await handleResponseArray(response, (entry: any) => {
    // @ts-ignore
    return new (getEntityType(entry._type).type.type)(entry);
  });
};

const getFastTrackStatus = async () => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/fast-track-status`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => result);
};

const testFastTrack = async (testData: {
  request: string;
  toolId: string;
  toolDescription: string;
}) => {
  const response = await authenticatedFetch(`${baseUrl}/api/test-fast-track`, {
    method: "POST",
    body: JSON.stringify(testData),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return handleResponse(response, (result: any) => result);
};

const createServiceOrder = async (
  customerID: string,
  order: Partial<Order>,
) => {
  const response = await authenticatedFetch(`${baseUrl}/${customerID}/orders`, {
    method: "POST",
    body: JSON.stringify(order),
    headers: {
      "Content-Type": "application/json",
    },
  });

  return handleResponse(response, (result: any) => {
    return new Order(result as any);
  });
};

const getPaymentMethods = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/payment-methods`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return result;
  });
};

const addPaymentMethod = async (
  customerID: string,
  paymentMethodId: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/payment-methods`,
    {
      method: "POST",
      body: JSON.stringify({ paymentMethodId }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (response.ok) {
    const result = await response.json();
    return result;
  } else {
    const errorText = await response.text();
    throw new Error(
      `API call failed with status ${response.status}: ${errorText}`,
    );
  }
};

const removePaymentMethod = async (
  customerID: string,
  paymentMethodId: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/payment-methods/${paymentMethodId}`,
    {
      method: "DELETE",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return result;
  });
};

const setDefaultPaymentMethod = async (
  customerID: string,
  paymentMethodId: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/payment-methods/default`,
    {
      method: "POST",
      body: JSON.stringify({ paymentMethodId }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return result;
  });
};

const getPaymentSummary = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/payment-summary`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return result;
  });
};

const getPaymentTransactions = async (
  customerID: string,
  limit: number = 10,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/payment-transactions?limit=${limit}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleResponse(response, (result: any) => {
    return result;
  });
};

const createStripeAccount = async (customerID: string, accountData: any) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/stripe-account`,
    {
      method: "POST",
      body: JSON.stringify(accountData),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (response.ok) {
    const result = await response.json();
    return result;
  } else {
    const errorText = await response.text();
    throw new Error(
      `API call failed with status ${response.status}: ${errorText}`,
    );
  }
};

const getStripeAccount = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/stripe-account`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (response.ok) {
    const result = await response.json();
    return result;
  } else {
    const errorText = await response.text();
    throw new Error(
      `API call failed with status ${response.status}: ${errorText}`,
    );
  }
};

const updateStripeAccount = async (customerID: string, accountData: any) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/stripe-account`,
    {
      method: "PUT",
      body: JSON.stringify(accountData),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (response.ok) {
    const result = await response.json();
    return result;
  } else {
    const errorText = await response.text();
    throw new Error(
      `API call failed with status ${response.status}: ${errorText}`,
    );
  }
};

const linkStripeAccount = async (customerID: string, accountId: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/customers/${customerID}/stripe-account/link`,
    {
      method: "POST",
      body: JSON.stringify({ accountId }),
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (response.ok) {
    const result = await response.json();
    return result;
  } else {
    const errorText = await response.text();
    throw new Error(
      `API call failed with status ${response.status}: ${errorText}`,
    );
  }
};

// MFA Authenticator App setup
export const setupAuthenticatorApp = async () => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/mfa/auth-app/setup`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  if (!response.ok) throw new Error("Failed to start authenticator app setup");
  return response.json();
};

// MFA Authenticator App verify
export const verifyAuthenticatorApp = async (code: string, secret: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/api/mfa/auth-app/verify`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code, secret }),
    },
  );
  if (!response.ok) throw new Error("Failed to verify authenticator app code");
  return response.json();
};

// Disable MFA
export const disableMFA = async () => {
  const response = await authenticatedFetch(`${baseUrl}/api/mfa/disable`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
  });
  if (!response.ok) throw new Error("Failed to disable MFA");
  return response.json();
};

export const validateMFA = async (email: string, code: string) => {
  const response = await authenticatedFetch(`${baseUrl}/api/mfa/validate`, {
    method: "POST",
    body: JSON.stringify({ email, code }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  const result = (await handleAuthenticatedResponse(response)) as any;

  // Store JWT tokens if they are provided
  if (result.success && result.jwtToken) {
    jwtAuthService.setTokens({
      accessToken: result.jwtToken,
      refreshToken: result.refreshToken || "",
    });
  }

  return result;
};

export const resetPassword = async (email: string) => {
  const response = await fetch(`${baseUrl}/reset-password`, {
    method: "POST",
    body: JSON.stringify({ email }),
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      errorData.message || `HTTP error! status: ${response.status}`,
    );
  }

  return response.json();
};

const getAgenticServices = async () => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/agentic/tools`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const getUsageMetrics = async () => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/metrics/usage`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return response.json();
};

const getZoningBacklog = async () => {
  console.log("UserAPI: getZoningBacklog starting fetch");
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/backlog`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  console.log(
    "UserAPI: getZoningBacklog fetch finished, status:",
    response.status,
  );

  return handleAuthenticatedResponse(response);
};

const getZoningJobs = async () => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/jobs`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleAuthenticatedResponse(response);
};

const getZoningDbJobs = async (limit: number = 20) => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/jobs/db?limit=${limit}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleAuthenticatedResponse(response);
};

const getZoningDbJob = async (jobId: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/jobs/db/${jobId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleAuthenticatedResponse(response);
};

const getZoningDbLatestJob = async (kind: string = "any") => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/jobs/db/latest?kind=${encodeURIComponent(kind)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleAuthenticatedResponse(response);
};

const getZoningLoopLatestJob = async (jobType: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/jobs/loop/latest?type=${encodeURIComponent(jobType)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  return handleAuthenticatedResponse(response);
};

const downloadZoningLog = async (filename: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/logs/download/${filename}`,
    {
      method: "GET",
      // Ein Protokoll kann groß sein; es lädt, solange es lädt.
      signal: PATIENT_REQUEST,
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to download log: ${response.statusText}`);
  }

  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
};

const getZoningCoverageGrid = async () => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/coverage/grid`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return handleAuthenticatedResponse(response);
};

const getZoningCoverageCounts = async (rowStart: number, rowEnd: number) => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/coverage/counts?rowStart=${rowStart}&rowEnd=${rowEnd}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return handleAuthenticatedResponse(response);
};

const getZoningGermanyMap = async () => {
  const response = await authenticatedFetch(
    `${baseUrl}/administration/monitoring/zoning/germany-map`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );
  return handleAuthenticatedResponse(response);
};

const getSecurityDashboard = async () => {
  const response = await authenticatedFetch(
    `${baseUrl}/admin/security/dashboard`,
    {
      method: "GET",
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to fetch security dashboard: ${response.statusText}`,
    );
  }

  return response.json();
};

const unblockIP = async (ip: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/admin/security/unblock-ip`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ ip }),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to unblock IP: ${response.statusText}`);
  }

  return response.json();
};

// Jobs API functions
const getJobs = async () => {
  const response = await authenticatedFetch(`${baseUrl}/admin/jobs`, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
    },
  });

  if (!response.ok) {
    throw new Error(`Failed to fetch jobs: ${response.statusText}`);
  }

  return response.json();
};

const updateSecurityThresholds = async (thresholds: {
  highVolume?: number;
  highSuspicious?: number;
  highBlocked?: number;
}) => {
  const response = await authenticatedFetch(
    `${baseUrl}/admin/security/thresholds`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(thresholds),
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to update security thresholds: ${response.statusText}`,
    );
  }

  return response.json();
};

const resetSecurityStats = async () => {
  const response = await authenticatedFetch(
    `${baseUrl}/admin/security/reset-stats`,
    {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ confirm: "RESET_SECURITY_STATS" }),
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to reset security stats: ${response.statusText}`);
  }

  return response.json();
};

const getMeteringStatistics = async (
  customerID: string,
  from: Date,
  to: Date,
  signal?: AbortSignal,
) => {
  const fromISO = from.toISOString();
  const toISO = to.toISOString();
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/metering/outbound-statistics?from=${encodeURIComponent(fromISO)}&to=${encodeURIComponent(toISO)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // Endet mit der Ansicht: siehe `abortedRequests`.
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to get metering statistics: ${response.statusText}`,
    );
  }

  return response.json();
};

const getInboundMeteringStatistics = async (
  customerID: string,
  from: Date,
  to: Date,
  signal?: AbortSignal,
) => {
  const fromISO = from.toISOString();
  const toISO = to.toISOString();
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/metering/inbound-statistics?from=${encodeURIComponent(fromISO)}&to=${encodeURIComponent(toISO)}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
      // Endet mit der Ansicht: siehe `abortedRequests`.
      signal,
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to get inbound metering statistics: ${response.statusText}`,
    );
  }

  return response.json();
};

const getMarketplaceStatistics = async (customerID: string) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace/statistics`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to get marketplace statistics: ${response.statusText}`,
    );
  }

  return response.json();
};

const getMarketplaceOfferStatistics = async (
  customerID: string,
  offerId: string,
) => {
  const response = await authenticatedFetch(
    `${baseUrl}/${customerID}/marketplace/statistics/${offerId}`,
    {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
      },
    },
  );

  if (!response.ok) {
    throw new Error(
      `Failed to get marketplace offer statistics: ${response.statusText}`,
    );
  }

  return response.json();
};

export { updateUserCredentials, getAgenticServices };

export const userAPI = {
  getTheme,
  matchBuilding,
  ensureBuilding,
  getDIN276Metadata,
  getDIN276MetadataFull,
  invalidateDIN276Metadata,
  getGeocodeAutocomplete,
  getPlatformServicesMarketplaceOffer,
  getPostalCode,
  getResourceURL,
  getFileURL,
  getChartImageURL,
  getChartImageURLWithAuth,
  storeChartImage,
  getResourceImageURL,
  getResourceImageURLWithAuth,
  storeResourceImage,
  getCustomer,
  getCustomerTools,
  getUsers,
  getBridges,
  getBuildings,
  submitRequest,
  streamPromptResponse,
  triggerEvent,
  checkEmail,
  checkUser,
  validateEmail,
  registerAndOrder,
  login,
  checkAuthentication,
  sendOTP,
  checkOTP,
  updateUserCredentials,
  logout,
  getCustomerSettings,
  sendEmail,
  sendSMS,
  getMarkup,
  query,
  queryOne,
  queryNoSQL,
  queryTelemetry,
  queryZip,
  getSkills,
  getSampleRequests,
  getChatbotIdeas,
  optimizeKnowledgeBaseDescription,
  getEntityByID,
  getProductStubs,
  getProductPage,
  getProductDistributions,
  getProductPhotoPage,
  updateProductPhoto,
  confirmProductPhotos,
  getProductsOfProjects,
  getUsageClasses,
  getCityObjects,
  getBuildingsWith3D,
  getVegetation,
  getSealedSurfaces,
  startVegetation,
  getBuildingModels,
  getBuildingMassing,
  saveBuildingMassing,
  getBuildingMassingContext,
  uploadBuildingModel,
  updateBuildingModelPlacement,
  deleteBuildingModel,
  setBuildingModelColourMode,
  getCityObject,
  getDeviceTypeCatalog,
  getDeviceConfiguration,
  updateDeviceConfiguration,
  queryDeviceConfiguration,
  submitSupportRequest,
  createSite,
  updateSite,
  deleteSite,
  createPartnership,
  updatePartnership,
  deletePartnership,
  getRoles,
  createRole,
  deleteRole,
  createRoleAssignment,
  deleteRoleAssignment,
  updateAsset,
  getProcessDefinitions,
  getChecklists,
  createChecklist,
  updateChecklist,
  deleteChecklist,
  startChecklist,
  getChecklistInstances,
  startWorkItemChecklistInstance,
  updateWorkItemChecklistInstance,
  getProcessInstanceChecklistInstances,
  getTasks,
  getOpenTasks,
  activateTask,
  deactivateTask,
  deactivateAndSaveTask,
  completeTask,
  abortTask,
  taskStatistics,
  fullOrderForProject,
  getInvoiceForecastForProject,
  runBillingForProject,
  createMissingInvoicesForProject,
  getConsumerOrders,
  getProviderOrders,
  getCustomerBillingInfo,
  updateCustomerBillingInfo,
  sampleDXF,
  uploadFile,
  uploadDocument,
  deleteDocument,
  classifyImageDIN276,
  assetPhotoDummy,
  validateBpmn,
  getBpmSchedules,
  askConversation,
  getConversationAskThread,
  clearConversationAskThread,
  activateBpmSchedule,
  deactivateBpmSchedule,
  getBpmScheduleFireHistory,
  getProcessInstances,
  getProcessInstance,
  deleteProcessInstance,
  deleteAllProcessInstances,
  getBusinessRules,
  createBusinessRule,
  updateBusinessRule,
  deactivateBusinessRule,
  activateBusinessRule,
  deleteBusinessRule,
  getConversations,
  getConversationList,
  getConversation,
  getConversationRun,
  createConversation,
  deleteConversation,
  interruptConversation,
  completeConversationTask,
  getConversationTaskSelectOptions,
  getConversationTaskForm,
  uploadContextDocuments,
  uploadSingleContextDocument,
  attachStorageContextDocuments,
  getContextDocumentJobStatus,
  getConversationFiles,
  uploadConversationFile,
  deleteContextDocument,
  getContextDocumentDownload,
  getDocumentByFileId,
  getManagedFileContent,
  signFileLink,
  createMarketplaceOffer,
  updateMarketplaceOffer,
  deleteMarketplaceOffer,
  publishMarketplaceOffer,
  getCustomerDetails,
  createMarketplaceOfferVoucher,
  sendMarketplaceOfferVoucherEmail,
  validateVoucher,
  unpublishMarketplaceOffer,
  submitMarketplaceOffer,
  appealAfterOfferRejection,
  refreshMarketplaceOffer,
  updateKnowledgeBaseImages,
  getMarketplaceOffers,
  getMarketplaceOfferById,
  getToolConfiguration,
  putToolConfiguration,
  getPublishedMarketplaceOffers: getPublicMarketplaceOffers,
  getFeaturedMarketplaceOffers,
  getFastTrackStatus,
  testFastTrack,
  createServiceOrder,
  getPaymentMethods,
  addPaymentMethod,
  removePaymentMethod,
  setDefaultPaymentMethod,
  getPaymentSummary,
  getPaymentTransactions,
  createStripeAccount,
  getStripeAccount,
  updateStripeAccount,
  linkStripeAccount,
  getResource,
  resetPassword,
  getAgenticServices,
  getUsageMetrics,
  getZoningBacklog,
  getZoningJobs,
  getZoningDbJobs,
  getZoningDbJob,
  getZoningDbLatestJob,
  getZoningLoopLatestJob,
  downloadZoningLog,
  getZoningCoverageGrid,
  getZoningCoverageCounts,
  getZoningGermanyMap,
  getSecurityDashboard,
  unblockIP,
  updateSecurityThresholds,
  resetSecurityStats,
  getMeteringStatistics,
  getInboundMeteringStatistics,
  getMarketplaceStatistics,
  getMarketplaceOfferStatistics,
  getJobs,
  storageAPI,
};

// MCP Proxy functionality
const mcpProxyCall = async (
  method: string,
  data?: any,
  targetUrl?: string,
  headers?: any,
) => {
  const requestData = targetUrl
    ? {
        target: targetUrl,
        data: data,
      }
    : data;

  const requestHeaders = {
    "Content-Type": "application/json",
    ...headers,
  };

  // Check if this is an internal endpoint
  const isInternalEndpoint =
    targetUrl &&
    (targetUrl.includes("/tri-") ||
      targetUrl.includes("api.the-real-insight.ai/tri-"));

  // Use different proxy endpoints based on whether it's internal or external
  const proxyEndpoint = isInternalEndpoint
    ? "/mcp-proxy/internal"
    : "/mcp-proxy";

  // Add timeout configuration for MCP calls
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 60000); // 60 second timeout for MCP calls

  try {
    const response = await authenticatedFetch(
      `${baseUrl}${proxyEndpoint}`,
      {
        method: method,
        headers: requestHeaders,
        body: requestData ? JSON.stringify(requestData) : undefined,
        signal: controller.signal,
      },
      // A 401 here means the proxied external MCP server needs auth — it must
      // NOT log the user out / redirect the app away from the editor.
      !isInternalEndpoint,
    );

    clearTimeout(timeoutId);

    // Extract session ID from response headers
    const sessionId =
      response.headers.get("mcp-session-id") ||
      response.headers.get("Mcp-Session-Id") ||
      response.headers.get("X-MCP-Session-Id");

    const result = await handleAuthenticatedResponse(
      response,
      (result: any) => {
        if (result.error) {
          throw new ApplicationError(result.error);
        }
        return result;
      },
    );

    // Return both the result and session ID
    return {
      result,
      sessionId,
    };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error instanceof Error && error.name === "AbortError") {
      throw new Error("MCP request timeout after 60 seconds");
    }
    throw error;
  }
};

// MCP Client wrapper for authenticated calls
class AuthenticatedMCPClient {
  private baseUrl: string;
  private targetUrl?: string;
  private sessionId?: string;
  private _authUrl: string | null = null;

  get authUrl() {
    return this._authUrl;
  }

  constructor(targetUrl?: string) {
    this.baseUrl = baseUrl;
    this.targetUrl = targetUrl;
  }

  async connect() {
    try {
      const response = await mcpProxyCall(
        "POST",
        {
          jsonrpc: "2.0",
          method: "initialize",
          id: 1,
          params: {
            protocolVersion: "2024-11-05",
            capabilities: {
              tools: {},
              resources: {},
              prompts: {},
            },
            clientInfo: {
              name: "tri-portal-client",
              version: "1.0.0",
            },
          },
        },
        this.targetUrl,
      );

      // Extract session ID from the response headers or response body
      if (response.sessionId) {
        this.sessionId = response.sessionId;
      } else if (response.result && response.result.sessionId) {
        this.sessionId = response.result.sessionId;
      }

      if (response.result && response.result.requiresAuth) {
        this._authUrl = response.result.authorizationUrl;
      }

      // Return the result, handling nested structure if present
      const result = response.result;
      if (result && result.result) {
        return result.result;
      }

      return result;
    } catch (error) {
      console.error("MCP connection failed:", error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to connect to MCP server: ${errorMessage}`);
    }
  }

  async listTools() {
    try {
      const headers: any = {};

      // Include session ID if available
      if (this.sessionId) {
        headers["mcp-session-id"] = this.sessionId;
      }

      const response = await mcpProxyCall(
        "POST",
        {
          jsonrpc: "2.0",
          method: "tools/list",
          id: 2,
          params: {},
        },
        this.targetUrl,
        headers,
      );

      // Handle different response formats
      const result = response.result;

      // Check if result has tools property
      if (result && result.tools && Array.isArray(result.tools)) {
        return result.tools;
      }

      // Fallback checks for other response formats
      if (Array.isArray(result)) {
        return result;
      }

      if (result && result.data && Array.isArray(result.data)) {
        return result.data;
      }

      // Check for nested result structure
      if (result && result.result) {
        const actualResult = result.result;
        if (
          actualResult &&
          actualResult.tools &&
          Array.isArray(actualResult.tools)
        ) {
          return actualResult.tools;
        }
        if (Array.isArray(actualResult)) {
          return actualResult;
        }
      }

      return result;
    } catch (error) {
      console.error("MCP listTools failed:", error);
      // Return empty array instead of throwing to prevent workflow editor from breaking
      return [];
    }
  }

  async callTool(toolName: string, arguments_: any) {
    try {
      const headers: any = {};

      // Include session ID if available
      if (this.sessionId) {
        headers["mcp-session-id"] = this.sessionId;
      }

      const response = await mcpProxyCall(
        "POST",
        {
          jsonrpc: "2.0",
          method: "tools/call",
          id: 3,
          params: {
            name: toolName,
            arguments: arguments_,
          },
        },
        this.targetUrl,
        headers,
      );

      // The server proxy may return the result in different formats
      const result = response.result;

      // If result has a result property (nested JSON-RPC response), extract it
      if (result && result.result) {
        return result.result;
      }

      // Otherwise return the result as is
      return result;
    } catch (error) {
      console.error(`MCP callTool failed for ${toolName}:`, error);
      const errorMessage =
        error instanceof Error ? error.message : String(error);
      throw new Error(`Failed to call tool ${toolName}: ${errorMessage}`);
    }
  }
}

// Export the new MCP functionality
export const mcpAPI = {
  createClient: (targetUrl?: string) => new AuthenticatedMCPClient(targetUrl),
  proxyCall: mcpProxyCall,
};

// ═══════════════════════════════════════════════════════════════════════════
// API Key Management
// ═══════════════════════════════════════════════════════════════════════════

export interface ApiKey {
  id: string;
  name: string;
  createdAt: string;
  expiresAt: string | null;
  lastUsedAt: string | null;
}

export interface CreateApiKeyResponse {
  accessToken: string;
  keyName: string;
  createdAt: string;
  expiresAt: string | null;
}

export const createApiKey = async (
  keyName: string,
  expiresInDays?: number,
): Promise<CreateApiKeyResponse> => {
  const response = await authenticatedFetch(`${baseUrl}/api-key`, {
    method: "POST",
    body: JSON.stringify({ keyName, expiresInDays }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    if (response.status === 409) {
      throw new ApplicationError("apiPanel.keyNameExists");
    }
    throw new Error(errorData.error || `Failed to create API key`);
  }

  return response.json();
};

export const listApiKeys = async (): Promise<ApiKey[]> => {
  const response = await authenticatedFetch(`${baseUrl}/api-key`, {
    method: "GET",
  });

  if (!response.ok) {
    throw new Error("Failed to list API keys");
  }

  const data = await response.json();
  return data.apiKeys;
};

export const deleteApiKey = async (
  keyId: string,
): Promise<{ message: string; keyId: string; keyName: string }> => {
  const response = await authenticatedFetch(`${baseUrl}/api-key/delete`, {
    method: "POST",
    body: JSON.stringify({ keyId }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || "Failed to delete API key");
  }

  return response.json();
};
